import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  ArrowShapeDownToLine,
  ArrowsRotateLeft,
  ArrowsRotateRight,
  ThumbsUp,
  ThumbsDown,
  ThumbsUpFill,
  ThumbsDownFill,
  PlayFill,
  PauseFill,
  ChevronLeft,
  ChevronRight,
  Clock,
  FaceFun,
  EllipsisVertical,
  CircleExclamation,
  Eye,
  ArrowUpFromSquare,
  Sliders,
  Check, // Import Check icon
} from "@gravity-ui/icons";
import JSZip from 'jszip';
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import { localState, useLocalState } from "../../contexts/LocalStateProvider";
import EditableInput from "./EditableInput";
import LoadingComponent from "./LoadingComponent";
import apiService from "../../api/apiService";
import { getCookie } from "../../utils/cookieUtils";
import Toast from "../generic_components/Toast";
import Modal from "../modal/Modal";
import FeedbackContent from "../modal/FeedbackContent"; // Assuming FeedbackContent is located in the modal folder
import ChatBoxLoader from "./ChatBoxLoader";
import { usePostHog } from "posthog-js/react";
import DownloadModal from "./DownloadModal";
import FeedbackPrompt from "./FeedbackPrompt";
import AudioProcessor from "./AudioProcessor";
import ShareModal from "../modal/ShareModal";
import ProgressInfoToast from "./ProgressInfoToast"; // Import the ProgressInfoToast component
import AdvancedEditModal from "../modal/AdvancedEditModal";
import { useGameContext } from "../../contexts/GameContext"; // Add this import at the top with other imports
import * as Sentry from "@sentry/react"
import { useAuth } from "../../contexts/AuthContext";
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";

const AIChatBox = ({
  message,
  relatedMessages = [],
  isLastMessage,
  loadingResponseId,
  beforeRegenerateLoaderId,
  streamingResponseId,
  onRegenerate,
  onErrorRegenerate,
  onBlankRegenerate,
  id,
  passedFromChatInterface,
  projectName,
  generationCount,
  projectId,
  onGenerationError,
  checkGenerationStatus,
  setTriggerTaskModal, // Receive the prop here
  // --- EDIT 1: Add onLoadingComplete prop ---
  onLoadingComplete
  // --- END EDIT 1 ---
}) => {
  // --- EDIT 1: Log the incoming message prop ---
  // console.log(`[AIChatBox ${message.ai_response_id}] Initial message prop:`, message);
  // --- END EDIT 1 ---

  // --- EDIT 1: Expand this log to see the full message object ---
  //console.log(`[AIChatBox ${message.ai_response_id}] Initial message prop FULL (after backend change):`, JSON.stringify(message, null, 2));
  // --- END EDIT 1 ---

  const {
    isPlaying,
    playAudio,
    pauseAudio,
    resumeAudio,
    stopAudio, // Added stopAudio to context
    audioSrc: currentAudioSrcFromContext, // Renamed to avoid conflict
    setAiResponseId,
    setDiscoverId,
    isBuffering: isBufferingFromContext, // Renamed to avoid conflict
    streamingSrc: streamingSrcFromContext, // Renamed to avoid conflict
    aiResponseId: contextAiResponseId, // Renamed to get the ai_response_id from context
    setIsBuffering,
    setStreamingSrc,
    currentTime,
    duration,
  } = useAudioPlayer();

  const posthog = usePostHog();
  const {isLoggedIn} = useAuth();
  const { localState, updateLocalState } = useLocalState();
  const { gamificationData, completeTask, fetchGamificationData } = useGameContext();
  const [showDurationInput, setShowDurationInput] = useState(false);
  const [showTempoInput, setShowTempoInput] = useState(false);
  const [regeneratingMessageId, setRegeneratingMessageId] = useState(null);
  // --- EDIT 1: Ensure responses state is correctly initialized and updated ---
  // The `message` prop is the *initial* message for this AIChatBox instance.
  // `relatedMessages` are other versions (e.g., regenerations) of this initial message.
  const [responses, setResponses] = useState(() => {
    const allVersions = [message, ...relatedMessages];
    // Sort by creation time or another relevant factor if needed, though usually already sorted.
    // Ensure no duplicates if message might also be in relatedMessages initially (should not happen).
    const uniqueVersions = Array.from(new Map(allVersions.map(item => [item.ai_response_id, item])).values());
    return uniqueVersions;
  });
  const [currentResponseIndex, setCurrentResponseIndex] = useState(() => {
    // Default to the latest response if multiple are passed initially
    // This assumes `responses` state above is correctly initialized.
    // If loadingResponseId is provided and matches one of the initial responses,
    // that one should ideally be the current one.
    // For simplicity now, let's default to the first one (which is `message` if no relatedMessages).
    // Or, if `message` itself is a regeneration, it might be the one to focus on.
    // The key is that `loadingResponseId` will drive which one *actually* loads.
    // Let's find the index of the initial `message` prop.
    const initialMsgIndex = responses.findIndex(r => r.ai_response_id === message.ai_response_id);
    return initialMsgIndex !== -1 ? initialMsgIndex : 0;
  });
  // --- END EDIT 1 ---
  const [likeStatus, setLikeStatus] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("error");
  const [newResponseAdded, setNewResponseAdded] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const [currentResponseisStreaming, setCurrentResponseIsStreaming] = useState(
    message.isStreaming || false
  );
  const [streamComplete, setStreamComplete] = useState(false);
  const [likeStatuses, setLikeStatuses] = useState({});
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false); // Modal state for small screen options
  const [pageWidth, setPageWidth] = useState(window.innerWidth);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [downloadOptions, setDownloadOptions] = useState(null);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showRegenerateTooltip, setShowRegenerateTooltip] = useState(false);
  const [showRegenerateSuggestionTooltip, setShowRegenerateSuggestionTooltip] = useState(false);
  const [showDownloadTooltip, setShowDownloadTooltip] = useState(false);
  const [showLikeTooltip, setShowLikeTooltip] = useState(false);
  const [showDislikeTooltip, setShowDislikeTooltip] = useState(false);
  const [showNodesRightTooltip, setShowNodesRightTooltip] = useState(false);
  const [showEyeTooltip, setShowEyeTooltip] = useState(false);
  const [showShareTooltip, setShowShareTooltip] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [actionLoadingText, setActionLoadingText] = useState("");
  const [suggestionsLoading, setSuggestionsLoading] = useState(false); // Add state for suggestions loading
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [stemTrackName, setStemTrackName] = useState('');
  const [generatedShareUrl, setGeneratedShareUrl] = useState(null); // State to hold the URL
  const [isSharing, setIsSharing] = useState(false); // Loading state for sharing
  const [shareError, setShareError] = useState(null); // Error state for sharing
  const [whatsNextEnabled, setWhatsNextEnabled] = useState(false);
  const [isAdvancedEditModalOpen, setIsAdvancedEditModalOpen] = useState(false);
  const [showAdvancedEditTooltip, setShowAdvancedEditTooltip] = useState(false);
  // State to track which track (1, 2, or 3) is currently playing, or null if none
  const [playingTrackIndex, setPlayingTrackIndex] = useState(null);
  // State to track selection status of each track
  const [selectedTracks, setSelectedTracks] = useState({ 0: false, 1: false, 2: false });
  // --- EDIT 1: Add state for the audio URI to pass to the modal ---
  const [audioUriForAdvancedEdit, setAudioUriForAdvancedEdit] = useState(null);
  const isPlayPauseActionPending = useRef(false);
  // --- END EDIT 1 ---
  const [trackStatuses, setTrackStatuses] = useState(() => {
    const initial = {};
    const msgToInit = responses[currentResponseIndex] || message; // Use the derived current or fallback
    const count = msgToInit.trackCount || 3;
    const isCurrentlyLoading = loadingResponseId === msgToInit.ai_response_id;

    for (let i = 0; i < count; i++) {
      if (msgToInit.is_error) {
        initial[i] = 'error';
      } else if (isCurrentlyLoading) {
        initial[i] = 'pending';
      } else if (msgToInit.audio) {
        initial[i] = 'complete';
      } else {
        initial[i] = 'pending';
      }
    }
    // console.log(`[AIChatBox Initial TrackStatus ${msgToInit.ai_response_id}] loadingResponseId: ${loadingResponseId}. Initial statuses:`, initial);
    return initial;
  });
  // --- END EDIT 6 ---

  // --- Autoplay State ---
  const [isAutoplaying, setIsAutoplaying] = useState(false);
  const [currentAutoplayTrackIndex, setCurrentAutoplayTrackIndex] = useState(null);
  const [autoplayInitiatedForThisMessage, setAutoplayInitiatedForThisMessage] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);

  // --- EDIT 1: Add a ref to track the ID for which autoplay was successfully set up ---
  const autoplaySetupForResponseIdRef = useRef(null);
  // --- END EDIT 1 ---
  
  // Ref to track previous response ID for auto-play
  const prevResponseIdRef = useRef(null);

  // Refs to store previous player state for end-of-track detection
  const prevIsPlaying = useRef(isPlaying);
  const prevAudioSrc = useRef(currentAudioSrcFromContext);

  // Memoize currentResponse to ensure it's stable unless inputs change
  const currentResponse = useMemo(() => {
    if (responses && responses.length > 0 && currentResponseIndex >= 0 && currentResponseIndex < responses.length) {
      const res = responses[currentResponseIndex];
      // console.log(`[AIChatBox currentResponse Memo ${res?.ai_response_id}] Derived. Index: ${currentResponseIndex}. Has requestId: ${!!res?.requestId}, Has request_id: ${!!res?.request_id}. Full message:`, JSON.stringify(res, null, 2));
      return res;
    }
    // console.warn(`[AIChatBox currentResponse Memo] Fallback to initial message. Index: ${currentResponseIndex}, Responses count: ${responses?.length}. Initial message ID: ${message?.ai_response_id}`);
    return message;
  }, [responses, currentResponseIndex, message]);

  // --- EDIT 2: Helper functions to check track statuses ---
  // Define helper functions
  const isAnyTrackStreaming = (statuses) => Object.values(statuses).some(s => s === 'streaming');
  // --- EDIT 14: Add helper to check if *any* track is loading (pending or streaming) ---
  const isAnyTrackLoading = (statuses) => Object.values(statuses).some(s => s === 'pending' || s === 'streaming');
  // --- END EDIT 14 ---
  const areAllTracksComplete = (statuses) => {
    const values = Object.values(statuses);
    // Check if there are statuses and if all are either complete or error
    // Consider 'error' as a form of completion for enabling actions
    return values.length > 0 && values.every(s => s === 'complete' || s === 'error');
  };
  // --- END EDIT 2 ---

  // Update prevIsPlaying and prevAudioSrc refs whenever their counterparts change
  // useEffect(() => { // Removed this effect, will be handled in the advancement effect
  //   prevIsPlaying.current = isPlaying;
  //   prevAudioSrc.current = currentAudioSrcFromContext;
  // }, [isPlaying, currentAudioSrcFromContext]);
  useEffect(() => {
    if (
      localState.selectedTrackDetails &&
      localState.selectedTrackDetails.responseId &&
      localState.versionHistory &&
      Array.isArray(localState.versionHistory)
    ) {
      const { responseId, trackIndex } = localState.selectedTrackDetails;

      // Update versionHistory
      const updatedHistory = localState.versionHistory.map(group => {
        let updatedGroup = { ...group };

        // Update latest if it matches and not already set
        if (
          group.latest &&
          group.latest.ai_response_id === responseId &&
          (group.latest.selected_track_id === undefined || group.latest.selected_track_id === null)
        ) {
          updatedGroup.latest = {
            ...group.latest,
            selected_track_id: trackIndex,
          };
        }

        // Update versions array if it matches and not already set
        if (Array.isArray(group.versions)) {
          updatedGroup.versions = group.versions.map(version =>
            version.ai_response_id === responseId &&
              (version.selected_track_id === undefined || version.selected_track_id === null)
              ? { ...version, selected_track_id: trackIndex }
              : version
          );
        }

        return updatedGroup;
      });

      updateLocalState("versionHistory", updatedHistory);
    }
    // eslint-disable-next-line
  }, [localState.selectedTrackDetails]);

  // Reset autoplay initiation flag if the main message ID changes
  useEffect(() => {
    setAutoplayInitiatedForThisMessage(false);
    setIsAutoplaying(false);
    setCurrentAutoplayTrackIndex(null);
  }, [message.ai_response_id]);

  // Add useEffect to manage whatsNextEnabled state
  useEffect(() => {
    // --- EDIT 3: Update whatsNextEnabled logic ---
    const allComplete = areAllTracksComplete(trackStatuses);
    const creditsAvailable = localState?.userProfileData?.remaining_credits !== null && localState?.userProfileData?.remaining_credits !== 0;
    const shouldEnable =
      currentResponse?.audio && // Ensure audio object exists
      allComplete && // Check if ALL tracks are complete or errored
      !localState.responseInProgress && // Ensure no global response is in progress
      creditsAvailable;

    // --- EDIT 15: Add detailed log for whatsNextEnabled calculation ---

    // --- END EDIT 15 ---
    // --- END EDIT 3 ---


    setWhatsNextEnabled(shouldEnable);
  }, [
    // ... existing dependencies ...
    trackStatuses, // Ensure it recalculates when statuses change
    // --- END EDIT 4 ---
    localState.responseInProgress,
    localState?.userProfileData?.remaining_credits,
  ]);

  // Add this near the beginning of the component
  useEffect(() => {
    if (localState.nextPromptSuggestion) {
      //console.log("nextPromptSuggestion type:", typeof localState.nextPromptSuggestion);
      //console.log("nextPromptSuggestion value:", localState.nextPromptSuggestion);
    }
  }, [localState.nextPromptSuggestion]);

  useEffect(() => {
    if (!projectId) return;

    // Check if target generation exists for this project
    if (projectId && !localState.projectFeedbackTargets?.[projectId]) {
      const randomTarget = Math.random() < 0.5 ? 3 : 6;
      updateLocalState("projectFeedbackTargets", {
        ...localState.projectFeedbackTargets,
        [projectId]: randomTarget,
      });
    }

    const targetGeneration = localState.projectFeedbackTargets?.[projectId];
    // console.log("generation count", generationCount);
    // console.log("target generation for project", targetGeneration);

    if (
      generationCount === targetGeneration &&
      isLastMessage &&
      currentResponse.audio
    ) {
      setShowFeedbackPrompt(true);
    }
  }, [
    generationCount,
    currentResponse,
    isLastMessage,
    localState.projectFeedbackTargets,
    projectId,
  ]);

  // Revised useEffect for Status Initialization and Polling
  useEffect(() => {
    const activeMessageForPolling = currentResponse || message;
    const activeMessageId = activeMessageForPolling.ai_response_id;
    const currentLoadingResponseId = loadingResponseId;
    const requestIdForPolling =
      activeMessageForPolling.requestId || activeMessageForPolling.request_id ||
      (activeMessageForPolling.audio?.src
        ? new URLSearchParams(activeMessageForPolling.audio.src.split("?")[1]).get("requestId")
        : null);
    const isTargetLoading = currentLoadingResponseId === activeMessageId;
    const expectedTrackCount = activeMessageForPolling.trackCount || 3;

    let pollingCleanupFunctions = [];

    // --- stopGeneration early exit ---
    if (localState.stopGeneration && currentLoadingResponseId === activeMessageId) {
      console.log(`[AIChatBox Loading Effect ${activeMessageId}] stopGeneration is true. Halting polling and signaling completion as stopped.`);

      pollingCleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') cleanup();
      });
      pollingCleanupFunctions = [];

      if (onLoadingComplete) {
        onLoadingComplete(activeMessageId, /* wasStopped */ true);
      }

      return; // 🚫 Exit early: no polling for stopped generation
    }

    // --- Polling Logic ---
    if (isTargetLoading && !activeMessageForPolling.is_error && isAnyTrackLoading(trackStatuses)) {
      if (requestIdForPolling) {
        for (let trackId = 0; trackId < expectedTrackCount; trackId++) {
          const currentStatus = trackStatuses?.[trackId];
          if (currentStatus !== 'complete' && currentStatus !== 'error') {
            const cleanupPolling = checkGenerationStatus(
              requestIdForPolling,
              activeMessageId,
              trackId,
              (status) => {
                setTrackStatuses(prev => {
                  const currentActive = responses?.[currentResponseIndex] || message;
                  if (currentActive.ai_response_id !== activeMessageId) return prev;
                  if (prev[trackId] === status) return prev;
                  return { ...prev, [trackId]: status };
                });
              }
            );
            if (typeof cleanupPolling === 'function') {
              pollingCleanupFunctions.push(cleanupPolling);
            }
          }
        }
      } else {
        // ❌ No requestId - mark pending/streaming as error
        setTrackStatuses(prev => {
          const newStatuses = { ...prev };
          let changed = false;
          for (let i = 0; i < expectedTrackCount; i++) {
            if (newStatuses[i] === 'pending' || newStatuses[i] === 'streaming') {
              newStatuses[i] = 'error';
              changed = true;
            }
          }
          return changed ? newStatuses : prev;
        });
      }
    } else if (!isTargetLoading && activeMessageForPolling.audio && !areAllTracksComplete(trackStatuses) && !isAnyTrackLoading(trackStatuses)) {
      // ✅ Mark remaining as complete
      setTrackStatuses(prev => {
        const newStatuses = { ...prev };
        let changed = false;
        for (let i = 0; i < expectedTrackCount; i++) {
          if (newStatuses[i] === 'pending' || newStatuses[i] === 'streaming') {
            newStatuses[i] = 'complete';
            changed = true;
          }
        }
        return changed ? newStatuses : prev;
      });
    }

    return () => {
      pollingCleanupFunctions.forEach(cleanup => {
        if (typeof cleanup === 'function') cleanup();
      });
      pollingCleanupFunctions = [];
    };
  }, [
    localState.stopGeneration,
    onLoadingComplete,
    loadingResponseId,
    currentResponse,
    currentResponseIndex,
    message,
    message.ai_response_id,
    message.trackCount,
    message.requestId,
    message.request_id,
    message.audio,
    message.is_error,
    checkGenerationStatus,
    trackStatuses,
    responses,
  ]);


  useEffect(() => {
    const allComplete = areAllTracksComplete(trackStatuses);
    if (allComplete) {
      updateLocalState("responseInProgress", false);
      console.log("response state updated");
      // --- Pause autoplay for Mac users when song is generated ---
      if (isAutoplaying && isMacUser()) {
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
        console.log("Autoplay paused for Mac user after song generation.");
      }
    }
  }, [trackStatuses]);

  // --- EDIT 2: Add Effect specifically for checking completion ---
  useEffect(() => {
    const currentMessageId = message.ai_response_id;
    // --- EDIT: Expanded logging before the main condition ---
    console.log(`[AIChatBox Completion Effect Check ${currentMessageId}] Running. loadingResponseId: ${loadingResponseId}, allComplete: ${areAllTracksComplete(trackStatuses)}, current local streamingId: ${streamingId}`);

    const isTargetLoading = loadingResponseId === currentMessageId;
    const allTracksForThisMessageAreComplete = areAllTracksComplete(trackStatuses);

    // Condition 1: This message WAS the target loading message, and it just completed.
    if (isTargetLoading && allTracksForThisMessageAreComplete) {
      console.log(`[AIChatBox Completion Effect ${currentMessageId}] Path A: THIS message was the target and IS NOW complete. Clearing all related states.`);
      setIsBuffering(false);
      setStreamingSrc(null);

      if (streamingId === currentMessageId) {
        console.log(`[AIChatBox Completion Effect ${currentMessageId}] Path A: Clearing local streamingId from ${streamingId} to null.`);
        setStreamingId(null);
      } else if (streamingId) {
        console.log(`[AIChatBox Completion Effect ${currentMessageId}] Path A: Local streamingId (${streamingId}) did not match currentMessageId (${currentMessageId}). Not clearing streamingId via Path A.`);
      }

      if (onLoadingComplete) {
        console.log(`[AIChatBox Completion Effect ${currentMessageId}] Path A: Calling onLoadingComplete for ${currentMessageId}.`);
        onLoadingComplete(currentMessageId);
      }
    }
    // Condition 2: This message is NOT the current target loading message (or there isn't one),
    // BUT its own tracks are complete, and it was previously streaming (its streamingId matches its ID).
    else if (allTracksForThisMessageAreComplete && streamingId === currentMessageId) {
      // --- EDIT: Add more specific log for Path B execution ---
      console.log(`[AIChatBox Completion Effect ${currentMessageId}] Path B: Tracks for THIS message are complete, and its streamingId ('${streamingId}') matches. Attempting to set streamingId to null.`);
      // --- END EDIT ---
      setStreamingId(null);
    }

  }, [
    trackStatuses,
    loadingResponseId,
    message.ai_response_id,
    onLoadingComplete,
    setIsBuffering,
    setStreamingSrc,
    streamingId,
    setStreamingId,
    // areAllTracksComplete is derived, not a direct dep
  ]);
  // --- END EDIT 2 ---

  const handleFeedbackDismiss = () => {
    setShowFeedbackPrompt(false);
    // if (!likeStatuses[currentResponse.ai_response_id]) {
    //   posthog.capture("Click on Dismiss Icon button on First Time Feedback");
    // }
  };

  const handleAdvancedEdit = useCallback(async () => { // Made async in previous step, keep it
    posthog.capture("Click on Advanced Edit Icon", {
      is_user_logged_in: true,
      songId: currentResponse.ai_response_id,
      songName: currentResponse.audio?.name || "Unknown",
    });

    const currentlySelectedTrackIndex = Object.keys(selectedTracks).find(index => selectedTracks[index] === true);

    if (currentlySelectedTrackIndex === undefined) {
      setToastMessage("Please select a track version to edit.");
      setToastType("info");
      return;
    }

    const numericTrackIndex = parseInt(currentlySelectedTrackIndex, 10);

    if (trackStatuses[numericTrackIndex] === 'error') {
      setToastMessage("This track version encountered an error and cannot be edited.");
      setToastType("error");
      return;
    }

    if (trackStatuses[numericTrackIndex] !== 'complete') {
      setToastMessage("Please wait for this track version to complete generation before editing.");
      setToastType("info");
      return;
    }


    // --- Task Completion Logic Start ---
    let taskCompleted = false;
    const taskKey = "use_advanced_editing"; // *** Replace with your actual task key if different ***
    try {
      const advancedEditTask = gamificationData?.tasks?.find(task => task.task_key === taskKey);
      if (advancedEditTask && !advancedEditTask.is_completed) {
        console.log(`Attempting to complete ${taskKey} task`);
        await completeTask(taskKey);
        console.log(`${taskKey} task completed successfully`);
        await fetchGamificationData(); // Refresh data after completion
        taskCompleted = true;
      } else {
        // console.log(`${taskKey} task already completed or not found`, advancedEditTask);
      }
    } catch (taskError) {
      console.error(`Error updating ${taskKey} task:`, taskError);
      Sentry.captureException(taskError);
    }
    // --- Task Completion Logic End ---



    // --- EDIT 1: Directly use music_file_url from currentResponse if available ---
    let uriToEdit = null;
    let gcsData = null;

    if (currentResponse && currentResponse.music_file_url && Array.isArray(currentResponse.music_file_url) && currentResponse.music_file_url[numericTrackIndex]) {
      uriToEdit = currentResponse.music_file_url[numericTrackIndex];
      console.log(`[AIChatBox handleAdvancedEdit] Using direct GCS URI from currentResponse.music_file_url[${numericTrackIndex}]: ${uriToEdit}`);
    }

    if (!uriToEdit) {
      // Fallback to fetching if not directly available or if the above assumption is wrong
      console.log(`[AIChatBox handleAdvancedEdit] GCS URI not directly available in currentResponse for track ${numericTrackIndex}. Fetching from backend.`);
      try {
        const gcsResponse = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${currentResponse.ai_response_id}/${numericTrackIndex}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
            }
          }
        );
        gcsData = await gcsResponse.json();

        if (!gcsData.success || !gcsData?.gcsUri) {
          setToastMessage(gcsData?.message || "Could not retrieve the audio URL for editing. Ensure the track selection is saved in DB.");
          setToastType("error");
          return;
        }
        uriToEdit = gcsData?.gcsUri;
      } catch (error) {
        console.error("Error fetching GCS URI for advanced edit:", error);
        Sentry.captureException(error); 
        setToastMessage("Failed to prepare audio for advanced editing. Please try again.");
        setToastType("error");
        // --- Trigger Task Modal Check ---
        if (taskCompleted && setTriggerTaskModal) {
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 5000);
        }
        return;
      }
      uriToEdit = gcsData.gcsUri;
    }

    if (uriToEdit) {
      setAudioUriForAdvancedEdit(uriToEdit);
      setIsAdvancedEditModalOpen(true);
    } else {
      // This case should ideally be caught by the checks above
      setToastMessage("Could not determine the audio URL for editing.");
      setToastType("error");
    }
    // --- END EDIT 1 ---
  }, [currentResponse, selectedTracks, trackStatuses, getCookie, setToastMessage, setToastType, posthog, setAudioUriForAdvancedEdit, setIsAdvancedEditModalOpen]);

  // Function to close the advanced edit modal
  const handleCloseAdvancedEditModal = () => {
    setIsAdvancedEditModalOpen(false);
    setAudioUriForAdvancedEdit(null); // Clear the URI when modal closes
  };
  // --- END EDIT 2 ---

  // useEffect(() => {
  //   setCurrentResponse(message);
  // }, [message]);

  // console.log("related messages", relatedMessages);

  // useEffect(() => {
  //   // Update responses state when message or relatedMessages change
  //   setResponses([message, ...relatedMessages]);
  //   console.log(responses);
  // }, [message, relatedMessages]);

  // Add useEffect to handle highlighting
  useEffect(() => {
    if (localState.scrollToAiResponse === message.ai_response_id) {
      setIsHighlighted(true);
      const element = document.getElementById(id);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });

      setTimeout(() => {
        setIsHighlighted(false);
        updateLocalState("scrollToAiResponse", null);
      }, 2000);
    }
  }, [localState.scrollToAiResponse, message.ai_response_id, id, updateLocalState]); // Added dependencies

  useEffect(() => {
    const handleResize = () => {
      setPageWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // --- EDIT 3: Refine how `responses` state is updated when `message` or `relatedMessages` props change ---
  useEffect(() => {
    const newAllVersions = [message, ...relatedMessages];
    // Create a map to ensure uniqueness by ai_response_id and to merge updates
    const messageMap = new Map();

    // Add initial message and related messages to the map
    newAllVersions.forEach(msg => {
      if (msg && msg.ai_response_id) { // Ensure msg and ai_response_id exist
        messageMap.set(msg.ai_response_id, { ...(messageMap.get(msg.ai_response_id) || {}), ...msg });
      }
    });

    // Also consider existing responses in the state for updates
    responses.forEach(existingMsg => {
      if (existingMsg && existingMsg.ai_response_id) { // Ensure msg and ai_response_id exist
        messageMap.set(existingMsg.ai_response_id, { ...existingMsg, ...(messageMap.get(existingMsg.ai_response_id) || {}) });
      }
    });

    const updatedUniqueResponses = Array.from(messageMap.values());

    // Sort them if necessary, e.g., by created_at, though usually parent passes them sorted.
    // For simplicity, we'll assume they are passed in a sensible order or ChatInterface manages order.

    let newResponsesAddedFlag = false;
    if (updatedUniqueResponses.length > responses.length) {
      newResponsesAddedFlag = true;
    }

    // Only update if there's an actual change to avoid loops
    if (JSON.stringify(updatedUniqueResponses) !== JSON.stringify(responses)) {
      // console.log(`[AIChatBox ResponsesEffect ${message.ai_response_id}] Updating responses state. Old count: ${responses.length}, New count: ${updatedUniqueResponses.length}`);
      setResponses(updatedUniqueResponses);
      if (newResponsesAddedFlag) {
        setNewResponseAdded(true); // This flag is used to switch to the latest version
      }
    }

  }, [message, relatedMessages]); // Removed `responses` from here to prevent potential loops if not careful
  // --- END EDIT 3 ---

  useEffect(() => {
    if (
      localState.editedSongDetails &&
      localState.editedSongDetails.ai_response_id ===
      responses[currentResponseIndex].ai_response_id
    ) {
      updateResponseDetails(localState.editedSongDetails);
    }
  }, [localState.editedSongDetails]);

  useEffect(() => {
    if (newResponseAdded) {
      setCurrentResponseIndex(responses.length - 1);
      setNewResponseAdded(false); // Reset the flag after toggling
    }
  }, [responses, newResponseAdded]);

  // useEffect(() => {
  //   if (message.like_count > 0) {
  //     setLikeStatus("like");
  //   } else if (message.dislike_count > 0) {
  //     setLikeStatus("dislike");
  //   } else {
  //     setLikeStatus(null);
  //   }
  // }, [message]);

  useEffect(() => {
    const newLikeStatuses = { ...likeStatuses };
    [message, ...relatedMessages].forEach((msg) => {
      if (!newLikeStatuses.hasOwnProperty(msg.ai_response_id)) {
        if (msg.like_count > 0) {
          newLikeStatuses[msg.ai_response_id] = "like";
        } else if (msg.dislike_count > 0) {
          newLikeStatuses[msg.ai_response_id] = "dislike";
        } else {
          newLikeStatuses[msg.ai_response_id] = null;
        }
      }
    });
    setLikeStatuses(newLikeStatuses);
  }, [message, relatedMessages]);

  useEffect(() => {
    if (
      localState.playLatestTrack === message.ai_response_id &&
      currentResponse.isStreaming &&
      currentResponse.audio
    ) {
      handleStreamingAudio();
    }
  }, []);

  const monitorGenerationStatus = async (requestId, aiResponseId) => {
    //console.log("Monitoring status for:", requestId, aiResponseId);

    const pollInterval = setInterval(async () => {
      try {
        for (let trackId = 0; trackId < 3; trackId++) {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_ENDPOINT}/api/check-generation-status/${requestId}/${trackId}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`,
              },
            }
          );

          const data = await response.json();

          if (!data.success) {
            console.log(
              "Calling onGenerationError with:",
              aiResponseId,
              data.message
            );

            clearInterval(pollInterval);
            onGenerationError(aiResponseId, data.message);
            return; // exit loop and polling
          }

          if (data.status === "ready") {
            console.log(`Track ${trackId} is ready.`);
            // Optional: trigger further handling here
          }
        }
      } catch (error) {
        console.error("Error checking generation status:", error);
        Sentry.captureException(error);
      }
    }, 2000);

    setTimeout(() => clearInterval(pollInterval), 20000);
  };

  const handleStreamingAudio = useCallback(async (trackId) => {
    const responseId = currentResponse.ai_response_id;
    console.log(`[AIChatBox handleStreamingAudio ${responseId}/${trackId}] Called. Forcing chunk stream path. isPlaying (context): ${isPlaying}, currentAudioSrc (context): ${currentAudioSrcFromContext}`);


    setStreamingId(responseId);
    setIsBuffering(true);
    const chunkStreamUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${currentResponse.requestId || responseId}/${trackId}`;
    console.log(`[AIChatBox handleStreamingAudio ${responseId}/${trackId}] Using chunk stream URL: ${chunkStreamUrl}. About to call setStreamingSrc & playAudio.`);
    setStreamingSrc(chunkStreamUrl);

    playAudio(
      chunkStreamUrl,
      currentResponse.audio?.name,
      currentResponse.musicDescription,
      currentResponse.avatar,
      currentResponse.audio.duration
    );
    console.log(`[AIChatBox handleStreamingAudio ${responseId}/${trackId}] playAudio called.`);

  }, [currentResponse, playAudio, setStreamingId, setIsBuffering, setStreamingSrc, isPlaying, currentAudioSrcFromContext]); // Added isPlaying & currentAudioSrcFromContext for logging
  const handlePlayPause = useCallback((trackIndex, isTriggeredByAutoplay = false) => {

    if (isPlayPauseActionPending?.current) {
      console.log(`[AIChatBox handlePlayPause ${currentResponse?.ai_response_id}/${trackIndex}] Action SKIPPED due to pending flag.`);
      return;
    }
    const responseId = currentResponse.ai_response_id;

    try {
      isPlayPauseActionPending.current = true;

      // Update selectedTrackDetails in localState to ensure other components know which track is selected
      if (!isTriggeredByAutoplay) {
        updateLocalState('selectedTrackDetails', {
          responseId: responseId,
          trackIndex: trackIndex
        });
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Updated selectedTrackDetails in localState`);
      }

      const requestId = currentResponse.requestId || responseId;
      const playbackUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${requestId}/${trackIndex}`;

      console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Determined playbackUrl: ${playbackUrl}. Comparing with context: currentAudioSrc: ${currentAudioSrcFromContext}, isPlaying: ${isPlaying}, local playingTrackIndex: ${playingTrackIndex}`);

      if (isPlaying && currentAudioSrcFromContext === playbackUrl && playingTrackIndex === trackIndex) {
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: PAUSE. Context isPlaying, src matches, local index matches.`);
        pauseAudio();
        setPlayingTrackIndex(null);
      }
      else if (!isPlaying && currentAudioSrcFromContext === playbackUrl && playingTrackIndex === trackIndex) {
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: RESUME. Context not isPlaying, src matches, local index matches.`);
        resumeAudio();
      }
      else {
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: PLAY NEW/SWITCH.`);
      }
      const currentTrackStatus = trackStatuses[trackIndex];
      let resolvedPlaybackUrl;

      if (currentTrackStatus === 'complete') {
        resolvedPlaybackUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${responseId}/${trackIndex}`;
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Track is COMPLETE. Using GCS stream URL: ${resolvedPlaybackUrl}`);
      } else {
        resolvedPlaybackUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${requestId}/${trackIndex}`;
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Track is ${currentTrackStatus}. Using CHUNK stream URL: ${resolvedPlaybackUrl}`);
      }

      console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Comparing with context: currentAudioSrc: ${currentAudioSrcFromContext}, isPlaying: ${isPlaying}, local playingTrackIndex: ${playingTrackIndex}`);

      let actionTaken = null;

      if (isPlaying && currentAudioSrcFromContext === resolvedPlaybackUrl && playingTrackIndex === trackIndex) {
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: PAUSE. Calling pauseAudio().`);
        pauseAudio();
        setPlayingTrackIndex(null);
        actionTaken = 'pause';
      }
      // --- EDIT 1: Simplify logic: If not pausing, always go to the PLAY NEW/SWITCH/RESTART path. ---
      // This avoids trying to "resume" a chunk stream that might have just ended.
      // The PLAY NEW/SWITCH/RESTART path will correctly choose between chunk stream or GCS stream.
      // else if (!isPlaying && currentAudioSrcFromContext === resolvedPlaybackUrl && currentTrackStatus !== 'complete') {
      //   console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: RESUME in-progress (${currentTrackStatus}) track.`);
      //   setPlayingTrackIndex(trackIndex); 
      //   resumeAudio();
      //   actionTaken = 'resume';
      // } 
      // --- END EDIT 1 ---

      if (!actionTaken) {
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Action: PLAY NEW/SWITCH/RESTART. Current track status: ${currentTrackStatus}`);

        if (currentTrackStatus === 'error') {
          console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Track has ERROR status. Cannot play.`);
          setToastMessage("This track encountered an error during generation.");
          setToastType("error");
          isPlayPauseActionPending.current = false;
          return;
        }

        if (!isPlaying && currentAudioSrcFromContext && currentAudioSrcFromContext !== resolvedPlaybackUrl) {
          console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Context has a different src (${currentAudioSrcFromContext}). Calling context.stopAudio() before playing new track.`);
          stopAudio();
        }

        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Setting AIResponseId in context to: ${currentResponse.ai_response_id}. Setting local playingTrackIndex to: ${trackIndex}.`);
        setAiResponseId(currentResponse.ai_response_id);
        setPlayingTrackIndex(trackIndex);
        const detailsForPlayingTrack = {
          name: currentResponse.audio?.name,
          description: currentResponse.musicDescription,
          coverImage: currentResponse.avatar,
          duration: currentResponse.audio?.duration,
          audioSrc: resolvedPlaybackUrl,
          ai_response_id: currentResponse.ai_response_id,
          projectId: currentResponse.projectId,
          playingAIChatBoxTrackIndex: trackIndex,
          isAutoplaying: isTriggeredByAutoplay // Add this field to distinguish autoplay from manual play
        };
        updateLocalState("currentSongDetails", detailsForPlayingTrack);

        if (currentTrackStatus === 'complete') {
          console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Playing COMPLETED track. Clearing local streamingId (if matches ${responseId}): ${streamingId}. Clearing context buffering (if matches src/id). Calling playAudio() with GCS stream URL: ${resolvedPlaybackUrl}.`);
          if (streamingId === responseId) setStreamingId(null);
          if (streamingSrcFromContext === resolvedPlaybackUrl || (contextAiResponseId === responseId && isBufferingFromContext)) {
            setIsBuffering(false);
            setStreamingSrc(null);
          }
          playAudio(
            resolvedPlaybackUrl,
            currentResponse.audio.name,
            currentResponse.musicDescription,
            currentResponse.avatar,
            currentResponse.audio.duration
          );
        } else {
          console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Playing IN-PROGRESS track. Calling handleStreamingAudio(${trackIndex}).`);
          handleStreamingAudio(trackIndex);
        }
      }

      setTimeout(() => {
        pageWidth >= 768 && updateLocalState("isDetailsPanelOpen", true);
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Updating localState currentPlayingResponseId to: ${currentResponse.ai_response_id}`);
        updateLocalState(
          "currentPlayingResponseId",
          currentResponse.ai_response_id
        );
      }, 50);
      setDiscoverId(null);

    }
    finally {
      setTimeout(() => {
        isPlayPauseActionPending.current = false;
        console.log(`[AIChatBox handlePlayPause ${responseId}/${trackIndex}] Pending flag RESET to false.`);
      }, 150); // Consider adjusting this timeout if race conditions persist, though 150-250ms is usually safe.
    }

    setTimeout(() => {
      pageWidth >= 768 && updateLocalState("isDetailsPanelOpen", true);
      updateLocalState(
        "currentPlayingResponseId",
        currentResponse.ai_response_id
      );
    }, 50);
    setDiscoverId(null);
  }

    , [
      currentTime, duration,
      currentResponse,
      responses,
      currentResponseIndex,
      trackStatuses,
      isPlaying,
      currentAudioSrcFromContext,
      playingTrackIndex,
      setPlayingTrackIndex,
      isAutoplaying,
      setIsAutoplaying,
      setCurrentAutoplayTrackIndex,
      pageWidth,
      updateLocalState,
      setDiscoverId,
      setAiResponseId,
      pauseAudio,
      resumeAudio,
      stopAudio,
      handleStreamingAudio,
      setToastMessage,
      setToastType
    ]);


  const updateResponseDetails = (newDetails) => {
    setResponses((prevResponses) => {
      const updatedResponses = [...prevResponses];
      updatedResponses[currentResponseIndex] = {
        ...updatedResponses[currentResponseIndex],
        audio: {
          ...updatedResponses[currentResponseIndex].audio,
          name:
            newDetails.name ||
            updatedResponses[currentResponseIndex].audio.name,
          src:
            newDetails.audioSrc ||
            updatedResponses[currentResponseIndex].audio.src,
          duration:
            newDetails.duration ||
            updatedResponses[currentResponseIndex].audio.duration,
        },
        musicDescription:
          newDetails.description ||
          updatedResponses[currentResponseIndex].musicDescription,
        avatar:
          newDetails.coverImage ||
          updatedResponses[currentResponseIndex].avatar,
      };
      return updatedResponses;
    });

    // console.log(responses, "responses");
  };

  const sendPromptDirectly = async (prompt) => {
    try {
      setIsActionLoading(true);
      setActionLoadingText(prompt);

      const currentProjectId = currentResponse.projectId || projectId;
      const userId = getCookie("user_id")  ||getPartitionedLocalStorage("user_id");
      const selectedTrackId = Object.entries(selectedTracks).find(([_, isSelected]) => isSelected)?.[0];

      if (localState?.userProfileData?.remaining_credits <= 0) {
        if (localState.isAdmin && localState.isTeamMember) {
          dispatch(setRedirectAfterSelectedPlan("/create-team"));
          dispatch(setSubscriptionModal(true));
          dispatch(setSubscriptionTypeModal("upgrade"));
        } else if (!localState.isTeamMember) {
          dispatch(setRedirectAfterSelectedPlan("/create-team"));
          dispatch(setSubscriptionModal(true));
        }
        setIsActionLoading(false);
        return;
      }


      const formData = new FormData();
      formData.append("project_id", currentProjectId);
      formData.append("user_id", userId);
      formData.append("content", prompt);
      formData.append("sentAt", new Date().toISOString());
      formData.append("message_type", "text");
      formData.append("trackCount", 3);
      formData.append("trackId", selectedTrackId || 0);
      // formData.append("regeneratingResponseId", "");

      const result = await apiService.storeUserMessage(formData);

      if (result.success) {
        // setToastMessage(result.success);
        // setToastType("success");

        // let image_bytes = null;
        // let audio_bytes = null;
        // let video_bytes = null;
        // let key = null;

        // if (key === "image_bytes") {
        //   image_bytes = fileContent;
        // } else if (key === "audio_bytes") {
        //   audio_bytes = fileContent;
        // } else if (key === "video_bytes") {
        //   video_bytes = fileContent;
        // }

        // const userInput = {
        //   image_bytes,
        //   audio_bytes,
        //   video_bytes,
        // };

        formData.append("message_id", result.message_id);
        // formData.append("userInput", userInput);
        // formData.append("sentAt", new Date().toISOString());
        formData.append("prompt", prompt);

        updateLocalState("newMessage", formData);
        posthog.capture("What's Next Action Clicked", {
          is_user_logged_in: true,
          prompt: prompt,
          projectId: currentProjectId,
          success: true,
        });
      } else {
        throw new Error(result.error || "Failed to process your request");
      }
    } catch (error) {
      console.error("Error sending prompt:", error);
      Sentry.captureException(error);
      setToastMessage(error.message || "Failed to process your request");
      setToastType("error");
      posthog.capture("What's Next Action Error", {
        is_user_logged_in: true,
        prompt: prompt,
        error: error.message,
      });
    } finally {
      setTimeout(() => {
        setIsActionLoading(false);
        setActionLoadingText("");
      }, 500);
    }
  };

  const handleActionClick = (action) => {
    if (!whatsNextEnabled) return;

    posthog.capture(`Click on Suggestion button chat page`, {
      is_user_logged_in: true,
      action: action,
    });
    // Send the prompt directly instead of updating the text input
    sendPromptDirectly(action);
  };

  // console.log(message.text);

  // Update the handleAudioClick function
  const handleAudioClick = () => {
    const detailsData = {
      name: currentResponse.audio.name,
      description: currentResponse.musicDescription,
      coverImage: currentResponse.avatar,
      duration: currentResponse.audio.duration,
      audioSrc: currentResponse.audio.src,
      ai_response_id: currentResponse.ai_response_id,
      projectId: currentResponse.projectId,
    };

    // Close preview panel if open
    updateLocalState("isPreviewPanelOpen", false);

    setTimeout(() => {
      if (!localState.isDetailsPanelOpen) {
        updateLocalState("isDetailsPanelOpen", true);
        posthog.capture("Click to open Details Panel",{
          is_user_logged_in: true
        });
      }
    }, 100);
    updateLocalState("currentSongDetails", detailsData); // Store details in local state
  };

  // ... existing code ...
  const handleLikeDislike = async (isLike) => {
    const userId = getCookie("user_id") ||getPartitionedLocalStorage("user_id");
    const currentResponse = responses[currentResponseIndex];

    // --- EDIT 1: Get the selected track index ---
    // selectedTrackIndex will be '0', '1', or '2' because the buttons are disabled otherwise
    const selectedIndex = parseInt(selectedTrackIndex, 10);
    // --- END EDIT 1 ---

    if (!userId || !currentResponse.ai_response_id || selectedIndex === undefined || isNaN(selectedIndex)) { // Add check for valid selectedIndex
      console.error("Cannot like/dislike: Missing user ID, response ID, or valid selected track index.", { userId, responseId: currentResponse.ai_response_id, selectedIndex });
      setToastMessage("Please select a track first.");
      setToastType("info");
      return;
    }


    const responseId = currentResponse.ai_response_id;
    const currentStatus = likeStatuses[responseId]; // 'like', 'dislike', or null
    let targetStatus = null; // 'like', 'dislike', or null

    if (isLike) {
      // User clicked the 'like' button
      targetStatus = currentStatus === "like" ? null : "like"; // Toggle like/null
    } else {
      // User clicked the 'dislike' button
      targetStatus = currentStatus === "dislike" ? null : "dislike"; // Toggle dislike/null
    }

    // Prepare payload for the update endpoint
    const payload = {
      response_id: responseId,
      user_id: userId,
      is_like: targetStatus === "like",
      dislike_reason: targetStatus === "dislike" ? "Not what I wanted" : null,
      // --- EDIT 2: Add selected_track_id to the payload ---
      selected_track_id: selectedIndex
      // --- END EDIT 2 ---
    };

    try {
      // Always call the update endpoint
      console.log("Sending like/dislike payload:", payload); // Log the payload
      await apiService.updateAIResponseLike(payload);

      // --- Update Local State ---
      const updatedLikeStatuses = { ...likeStatuses, [responseId]: targetStatus };
      setLikeStatuses(updatedLikeStatuses);

      // Update local tracks and if needed, fetch from server to refresh
      if (targetStatus === "like") {
        // Logic for adding a like
        posthog.capture("AI Chatbox Song Like/Dislike", { is_user_logged_in: true,LikeStatus: "like" });

        // After liking, fetch the updated list from server
        try {
          const likedTracks = await apiService.handleProtectedRoute("fetchLikedProjectTracks", {});
          if (likedTracks && Array.isArray(likedTracks)) {
            const processedData = likedTracks.map((track) => {
              // Log the entire track object for inspection
              // console.log(`[AIChatBox LikedTrack] Raw track object from fetchLikedProjectTracks:`, JSON.parse(JSON.stringify(track)));

              let trackIndexToUse = 0; // Default to first track
              if (track.selected_track_id !== null &&
                track.selected_track_id !== undefined &&
                !isNaN(parseInt(track.selected_track_id, 10))) {
                const selectedId = parseInt(track.selected_track_id, 10);
                // Ensure selectedId is within a valid range, e.g., 0-2 for 3 tracks
                // This check might need adjustment if track.trackCount is available
                if (selectedId >= 0 && selectedId < (track.track_count || 3)) {
                  trackIndexToUse = selectedId;
                } else {
                  //console.warn(`[AIChatBox LikedTrack] Liked track ID: ${track.response_id}, Title: "${track.song_title || track.audio?.name || 'N/A'}" has invalid selected_track_id: ${track.selected_track_id} for track_count: ${track.track_count || 3}. Defaulting to track 0.`);
                }
              }

              let constructedAudioSrc;
              // Prioritize music_file_url if it's an array and the indexed element (a streamable URL) exists
              if (track.music_file_url && Array.isArray(track.music_file_url) && track.music_file_url[trackIndexToUse]) {
                constructedAudioSrc = track.music_file_url[trackIndexToUse];
              } else {
                // Fallback to original construction if music_file_url is not usable or not populated with streamable URLs
                constructedAudioSrc = track.is_streaming && track.request_id
                  ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${track.request_id}/${trackIndexToUse}`
                  : `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${track.response_id}/${trackIndexToUse}`;
              }

              //console.log(`[AIChatBox LikedTrack] Processing liked track: ID ${track.response_id}, Title: "${track.song_title || track.audio?.name || 'N/A'}". DB selected_track_id: ${track.selected_track_id}. Resolved to play track index: ${trackIndexToUse}. Audio source: ${constructedAudioSrc}`);
              // console.log(`[AIChatBox LikedTrack] Full track data being processed:`, JSON.parse(JSON.stringify(track)));


              return {
                ...track,
                audioSrc: constructedAudioSrc,
                imageSrc: track.signedUrlExists
                  ? track.cover_image_name
                  : !track.signedUrlExists && track.cover_image_name
                    ? `/project-images/${track.cover_image_name}`
                    : "/project-images/Album_Art_6.webp",
              };
            });

            updateLocalState("likedProjectTracks", processedData);
          }
        } catch (error) {
          console.error("Error fetching updated liked tracks:", error);
          Sentry.captureException(error);
        }
      } else {
        if (targetStatus === "dislike") {
          posthog.capture("AI Chatbox Song Like/Dislike", { LikeStatus: "dislike",is_user_logged_in: true });

          // Specifically for dislike, ensure the feedback modal opens
          setIsFeedbackModalOpen(true);

          // Update user profile to get refreshed credit amount
          try {
            const userProfile = await apiService.handleProtectedRoute("fetchUserProfile", {});
            if (userProfile) {
              updateLocalState("userProfileData", userProfile);
            }
          } catch (error) {
            console.error("Error refreshing user profile after dislike:", error);
            Sentry.captureException(error);
          }
        }

        // After unliking/disliking, fetch the updated list from server
        try {
          const likedTracks = await apiService.handleProtectedRoute("fetchLikedProjectTracks", {});
          if (likedTracks && Array.isArray(likedTracks)) {
            const processedData = likedTracks.map((track) => {
              // Log the entire track object for inspection
              // console.log(`[AIChatBox LikedTrack] Raw track object from fetchLikedProjectTracks (dislike path):`, JSON.parse(JSON.stringify(track)));

              let trackIndexToUse = 0; // Default to first track
              if (track.selected_track_id !== null &&
                track.selected_track_id !== undefined &&
                !isNaN(parseInt(track.selected_track_id, 10))) {
                const selectedId = parseInt(track.selected_track_id, 10);
                // Ensure selectedId is within a valid range
                if (selectedId >= 0 && selectedId < (track.track_count || 3)) {
                  trackIndexToUse = selectedId;
                } else {
                  //console.warn(`[AIChatBox LikedTrack] Liked track ID: ${track.response_id}, Title: "${track.song_title || track.audio?.name || 'N/A'}" has invalid selected_track_id: ${track.selected_track_id} for track_count ${track.track_count || 3}. Defaulting to track 0.`);
                }
              }

              let constructedAudioSrc;
              // Prioritize music_file_url if it's an array and the indexed element (a streamable URL) exists
              if (track.music_file_url && Array.isArray(track.music_file_url) && track.music_file_url[trackIndexToUse]) {
                constructedAudioSrc = track.music_file_url[trackIndexToUse];
              } else {
                // Fallback to original construction if music_file_url is not usable or not populated with streamable URLs
                constructedAudioSrc = track.is_streaming && track.request_id
                  ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${track.request_id}/${trackIndexToUse}`
                  : `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${track.response_id}/${trackIndexToUse}`;
              }

              //console.log(`[AIChatBox LikedTrack] Processing liked track: ID ${track.response_id}, Title: "${track.song_title || track.audio?.name || 'N/A'}". DB selected_track_id: ${track.selected_track_id}. Resolved to play track index: ${trackIndexToUse}. Audio source: ${constructedAudioSrc}`);
              // console.log(`[AIChatBox LikedTrack] Full track data being processed (dislike path):`, JSON.parse(JSON.stringify(track)));

              return {
                ...track,
                audioSrc: constructedAudioSrc,
                imageSrc: track.signedUrlExists
                  ? track.cover_image_name
                  : !track.signedUrlExists && track.cover_image_name
                    ? `/project-images/${track.cover_image_name}`
                    : "/project-images/Album_Art_6.webp",
              };
            });

            updateLocalState("likedProjectTracks", processedData);
          }
        } catch (error) {
          console.error("Error fetching updated liked tracks after dislike:", error);
          Sentry.captureException(error);
        }
      }
      // --- End Local State Update ---

    } catch (error) {
      console.error("Error updating like/dislike:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to update like/dislike status.");
      setToastType("error");
    }
  };
  // console.log("Local state", localState);
  // Original Approach
  // const triggerDownloadModal = async () => {
  //   const currentResponseId = currentResponse.ai_response_id;
  //   const allComplete = areAllTracksComplete(trackStatuses);

  //   if (!allComplete) {
  //     setToastMessage("Audio is still processing; download is not available yet.");
  //     setToastType("error");
  //     return;
  //   }

  //   // Check if the current response has any song selected
  //   if (localState?.selectedTrackDetails ) {
  //     // --- EDIT: Add log to check currentResponse.ai_response_id type and value ---
  //     console.log(`[AIChatBox triggerDownloadModal] Preparing to open DownloadModal. currentResponse.ai_response_id type: ${typeof currentResponseId}, value: ${JSON.stringify(currentResponseId)}`);
  //     // --- END EDIT ---

  //     // --- Existing logs and logic ---
  //     console.log(`[AIChatBox triggerDownloadModal ${currentResponseId}] Attempting download. Current local streamingId: ${streamingId}. Calculated allComplete: ${allComplete}.`);
  //     console.log(`[AIChatBox triggerDownloadModal ${currentResponseId}] Track statuses:`, JSON.stringify(trackStatuses, null, 2));

  //     // If not all tracks are complete AND the current streamingId matches this response,
  //     // then it's genuinely still streaming or processing this response.
  //     if (!allComplete && streamingId === currentResponseId) {
  //       console.warn(`[AIChatBox triggerDownloadModal ${currentResponseId}] Download prevented: Tracks are NOT all complete ('${allComplete}') AND local streamingId ('${streamingId}') matches current response ID ('${currentResponseId}').`);
  //       setToastMessage("Audio is still streaming or processing; download is not available yet.");
  //       setToastType("error");
  //       return;
  //     }

  //     // If all tracks ARE complete, download should be allowed.
  //     // Or, if streamingId does not match this response (meaning some other response is streaming, or none), download for this should be allowed if it's not the one actively streaming.
  //     // The primary gate is active streaming *of this specific item*. If all its tracks are done, it's not actively streaming anymore.
  //     console.log(`[AIChatBox triggerDownloadModal ${currentResponseId}] Proceeding to open download modal. allComplete: ${allComplete}, streamingId: ${streamingId}, currentResponseId: ${currentResponseId}`);
  //     // --- END EDIT ---
  //     setIsDownloadModalOpen(true);
  //   }
  //   else if (!localState?.selectedTrackDetails || localState?.responseInProgress == false) {
  //     try {
  //       setIsDownloading(true);
  //       // Create URLs for all three tracks
  //       console.log("Inside Download three");
  //       const tracks = [];
  //       for (let i = 0; i < 3; i++) {
  //         if (trackStatuses[i] === 'complete') {
  //           const trackUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${currentResponseId}/${i}`;
  //           tracks.push({
  //             url: trackUrl,
  //             name: `${currentResponse.audio.name}_v${i + 1}.mp3`
  //           });
  //         }
  //       }

  //       // Create a zip file using JSZip
  //       const JSZip = require('jszip');
  //       const zip = new JSZip();
  //       // Download each track and add to zip
  //       const token = getCookie("token");
  //       if (!token) {
  //         setToastMessage("Authorization token missing. Please log in again.");
  //         setToastType("error");
  //         return;
  //       }
  //       for (const track of tracks) {

  //         const response = await fetch(track.url, {
  //           headers: {
  //             'Authorization': `Bearer ${token}`
  //           }
  //         });
  //         const blob = await response.blob();
  //         zip.file(track.name, blob);
  //       }

  //       // Generate zip file after all tracks are added
  //       const zipBlob = await zip.generateAsync({ type: "blob" });

  //       // Create download link
  //       const url = window.URL.createObjectURL(zipBlob);
  //       const a = document.createElement('a');
  //       a.href = url;
  //       a.download = `${currentResponse.audio.name}_all_versions.zip`;

  //       // Trigger download
  //       document.body.appendChild(a);
  //       a.click();

  //       // Cleanup
  //       window.URL.revokeObjectURL(url);
  //       document.body.removeChild(a);

  //       setToastMessage("Download started successfully!");
  //       setToastType("success");

  //     } catch (error) {
  //       console.error('Download error:', error);
  //       setToastMessage("Failed to download tracks. Please try again.");
  //       setToastType("error");
  //     } finally {
  //       setIsDownloading(false);
  //     }
  //     return;
  //   }
  // };

  const triggerDownloadModal = async () => {
    posthog.capture("Click-Download-AIChatbox-ChatPage", {
      is_user_logged_in: true
    });
    const currentResponseId = currentResponse.ai_response_id;
    const allComplete = areAllTracksComplete(trackStatuses);

    console.log("Current response ID:", currentResponseId);

    if (!allComplete) {
      setToastMessage("Audio is still processing; download is not available yet.");
      setToastType("error");
      return;
    }

    let selectedTrackId = null;

    console.log("Selected track details:", localState?.selectedTrackDetails);

    if (currentResponseId == localState?.currentSongDetails?.ai_response_id) {
      console.log("Current song matched with the last geenration")
      // this is the current song 
      if (localState?.selectedTrackDetails?.responseId === currentResponseId) {
        selectedTrackId = localState?.selectedTrackDetails?.trackIndex;
        setIsDownloadModalOpen(true);
        return;
      }
    } else if (localState?.selectedTrackDetails?.responseId === currentResponseId) {
      // this is the current song 
      selectedTrackId = localState?.selectedTrackDetails?.trackIndex;
      setIsDownloadModalOpen(true);
      return;
    }
    else {
      // 2. For history, check versionHistoryPersistent
      const historyEntry = localState?.versionHistory?.find(group =>
        group.latest?.ai_response_id === currentResponseId ||
        group.versions?.some(version => version.ai_response_id === currentResponseId)
      );

      if (historyEntry) {
        // Check latest
        if (
          historyEntry.latest?.ai_response_id === currentResponseId &&
          historyEntry.latest.selected_track_id !== undefined &&
          historyEntry.latest.selected_track_id !== null
        ) {
          selectedTrackId = historyEntry.latest.selected_track_id;
        }
        // Check versions array if not found in latest
        if (selectedTrackId === null && Array.isArray(historyEntry.versions)) {
          const versionMatch = historyEntry.versions.find(
            v => v.ai_response_id === currentResponseId && v.selected_track_id !== undefined && v.selected_track_id !== null
          );
          if (versionMatch) {
            selectedTrackId = versionMatch.selected_track_id;
          }
        }
      }
    }

    if (selectedTrackId !== null && selectedTrackId !== undefined) {
      // Found a selected track, open modal for single track download
      setIsDownloadModalOpen(true);
      return;
    }

    // If no selected track, download all tracks as zip
    console.log("Downloading all tracks as zip")
    await downloadAllTracksAsZip();
  };
  // Helper function to download all tracks as zip
  const downloadAllTracksAsZip = async () => {
    try {
      setIsDownloading(true);
      const tracks = [];
      for (let i = 0; i < 3; i++) {
        if (trackStatuses[i] === 'complete') {
          const trackUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${currentResponse.ai_response_id}/${i}`;
          tracks.push({
            url: trackUrl,
            name: `${currentResponse.audio.name}_v${i + 1}.mp3`
          });
        }
      }

      const zip = new JSZip();
      const token = getCookie("token") || getPartitionedLocalStorage("token");

      if (!token) {
        setToastMessage("Authorization token missing. Please log in again.");
        setToastType("error");
        return;
      }

      for (const track of tracks) {
        const response = await fetch(track.url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const blob = await response.blob();
        zip.file(track.name, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentResponse.audio.name}_all_versions.zip`;

      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setToastMessage("Download started successfully!");
      setToastType("success");

    } catch (error) {
      console.error('Download error:', error);
      Sentry.captureException(error);
      setToastMessage("Failed to download tracks. Please try again.");
      setToastType("error");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (localState.playLatestTrack === message.ai_response_id) {
      handlePlayPause(0);
      updateLocalState("playLatestTrack", null);
    }
  }, [localState.playLatestTrack]);

  const handlePreviousResponse = () => {
    if (currentResponseIndex > 0) {
      setCurrentResponseIndex(currentResponseIndex - 1);
    }
  };

  const handleNextResponse = () => {
    if (currentResponseIndex < responses.length - 1) {
      setCurrentResponseIndex(currentResponseIndex + 1);
    }
  };

  // If this response is the one currently loading, show the LoadingComponent
  // --- EDIT 6: Remove dedicated loading state return block ---
  // The loading indicators will now be rendered conditionally within the main return block based on trackStatuses
  // useEffect(() => { ... }); // Existing loading effect - KEEP THIS
  // if (tracksLoading) { ... } // REMOVE THIS BLOCK
  // --- END EDIT 6 ---

  const handleOptionsClick = () => {
    setIsOptionsModalOpen(true);
  };

  const handleOptionsClose = () => {
    setTimeout(() => {
      setIsOptionsModalOpen(false);
    }, 1000);
  };

  // Add a function to update song details without opening details panel
  // --- EDIT 1: Modify updateSongDetails to use the selected track index ---
  const updateSongDetails = (trackIndexToUse) => {
    // console.log(`[AIChatBox updateSongDetails] Called for trackIndex: ${trackIndexToUse}`);
    if (trackIndexToUse === undefined || trackIndexToUse === null) {
      console.warn("[AIChatBox updateSongDetails] trackIndexToUse is undefined or null. This might lead to issues.");
      // Optionally, default to 0 or handle as an error
      // For preview, it's better if a track is explicitly selected.
    }

    const responseId = currentResponse.ai_response_id;
    // For streaming, requestId is preferred. If not present, fall back to responseId.
    const idForStreaming = currentResponse.requestId || responseId;
    let audioSrcForDetails = null; // Initialize to null

    // Determine audioSrc based on track status and index
    const statusOfSelectedTrack = trackStatuses[trackIndexToUse];

    if (statusOfSelectedTrack === 'complete') {
      if (responseId) {
        audioSrcForDetails = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${responseId}/${trackIndexToUse}`;
      } else {
        console.error(`[AIChatBox updateSongDetails] Cannot form 'complete' audio URL: responseId is '${responseId}'. Setting audioSrc to null.`);
      }
    } else if (statusOfSelectedTrack === 'streaming' || statusOfSelectedTrack === 'pending') {
      if (idForStreaming) {
        audioSrcForDetails = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${idForStreaming}/${trackIndexToUse}`;
      } else {
        console.error(`[AIChatBox updateSongDetails] Cannot form 'streaming/pending' audio URL: idForStreaming is '${idForStreaming}' (derived from requestId='${currentResponse.requestId}', responseId='${responseId}'). Setting audioSrc to null.`);
      }
    } else if (statusOfSelectedTrack === 'error') {
      audioSrcForDetails = null;
      console.log(`[AIChatBox updateSongDetails] Track ${trackIndexToUse} for response ${responseId || 'UNKNOWN_ID'} has an error status. Audio src set to null.`);
    } else {
      // Fallback for unexpected status. It's better to set to null if IDs are problematic or status is unknown.
      console.warn(`[AIChatBox updateSongDetails] Unexpected track status: '${statusOfSelectedTrack}' for track ${trackIndexToUse}. ResponseId: '${responseId}', RequestId: '${currentResponse.requestId}'. Setting audioSrc to null as a precaution.`);
      audioSrcForDetails = null;
    }
    // console.log(`[AIChatBox updateSongDetails] Determined audioSrcForDetails for track ${trackIndexToUse}: ${audioSrcForDetails}`);

    const detailsData = {
      name: currentResponse.audio?.name, // Keep general name for now
      description: currentResponse.musicDescription,
      coverImage: currentResponse.avatar,
      duration: currentResponse.audio?.duration, // Keep general duration
      audioSrc: audioSrcForDetails, // Use the dynamically constructed or nulled src
      ai_response_id: responseId, // Pass responseId, even if it's undefined (PreviewPanel might use it for other things)
      projectId: currentResponse.projectId,
      // Potentially add trackIndex to detailsData if the preview panel needs to know
      selectedTrackIndexForPreview: trackIndexToUse
    };

    updateLocalState("currentSongDetails", detailsData); // Store details in local state
  };
  // --- END EDIT 1 ---

  // Add a handler function for the NodesRight icon click
  const handleStemToast = useCallback(async () => {
    // Track the click with PostHog
    //console.log("[AIChatBox] handleStemToast triggered for track:", currentResponse.audio.name);
    //console.log("[AIChatBox] Current response data:", currentResponse);
    posthog.capture("Click-GetStems-AIChatbox-ChatPage", {
      is_user_logged_in: true
    });
    console.log("[AIChatBox] handleStemToast triggered for track:", currentResponse.audio.name);
    console.log("[AIChatBox] Current response data:", currentResponse);

    // --- Start API Call Logic ---
    setIsGeneratingStems(true); // Show loading state
    setToastMessage(null); // Clear previous toasts

    // Log the request parameters

    // --- EDIT 1: Determine selectedTrackIndex ---
    const selectedTrackIndex = Object.keys(selectedTracks).find(index => selectedTracks[index] === true);

    if (selectedTrackIndex === undefined) {
      setToastMessage("Please select a track first to get stems.");
      setToastType("info");
      setIsGeneratingStems(false);
      return;
    }
    // --- END EDIT 1 ---

    try {
      // 1. Fetch GCS URI from the backend using direct GET request
      const gcsResponse = await fetch(
        // --- EDIT 2: Include selectedTrackIndex in the URL ---
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${currentResponse.ai_response_id}/${selectedTrackIndex}`,
        // --- END EDIT 2 ---
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}` // Assuming you have getCookie function
          }
        }
      );

      const gcsData = await gcsResponse.json();

      // Log the complete response for debugging


      if (!gcsData.success || !gcsData.gcsUri) {

        setToastMessage(gcsData.message || "Could not find the audio file URI needed for stem separation.");
        setToastType("error");
        setIsGeneratingStems(false);
        return;
      }

      const gcsUri = gcsData.gcsUri;
      //console.log(`[AIChatBox] Successfully extracted GCS URI: ${gcsUri}`);

      // 2. Call the /process-audio endpoint with the GCS URI
      const requestId = currentResponse.requestId || Date.now().toString();


      setToastMessage(`${currentResponse.audio.name || 'Track'} - Stems are being generated...`);
      setToastType("info");

      // Make direct fetch call to /process-audio
      const processResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/process-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({
            gcs_uri: gcsUri,
            request_id: requestId
          })
        }
      );

      const processData = await processResponse.json();


      if (processResponse.ok && processData.success) {
        //console.log("[AIChatBox] Stem separation successful:", processData);
        setToastMessage("Stems generated successfully! Downloading...");
        setToastType("info");

        // Extract stem paths
        const stemPaths = Object.values(processData.uploaded_files);
        setStemProgress(3); // Update progress to 3/3 when stems are ready for download

        // Download stems
        const downloadResponse = await fetch(
          `${process.env.REACT_APP_SERVER_ENDPOINT}/api/download-stems`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
            },
            body: JSON.stringify({
              stems: stemPaths
            })
          }
        );

        if (downloadResponse.ok) {
          setStemTrackName(currentResponse?.audio?.name);
          const finalName = currentResponse?.audio?.name || 'Audio';
          // Convert response to blob and trigger download
          const blob = await downloadResponse.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${finalName}_Stems_by_Wubble.zip`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          setToastMessage("Your stems have been generated and downloaded successfully!");
          setToastType("success");

          // Add these lines to complete the "generate_stems" task
          let taskCompleted = false;
          try {
            const stemTask = gamificationData?.tasks?.find(task => task.task_key === "generate_stems");
            if (stemTask && !stemTask.is_completed) {
              console.log("Attempting to complete generate stems task");
              await completeTask("generate_stems");
              console.log("Generate stems task completed successfully");
              await fetchGamificationData();
              taskCompleted = true;
            } else {
              console.log("Generate stems task already completed or not found", stemTask);
            }
          } catch (taskError) {
            console.error("Error updating generate_stems task:", taskError);
            Sentry.captureException(taskError);
          } finally {
            if (taskCompleted && setTriggerTaskModal) {
              setTriggerTaskModal(true);
              setTimeout(() => setTriggerTaskModal(false), 5000);
            }
          }
        } else {
          //console.error("[AIChatBox] Error downloading stems:", downloadResponse);
          setToastMessage("Stems generated but download failed. Please try again.");
          setToastType("error");
        }
      } else {

        setToastMessage(processData.message || processData.details || "Stem separation failed. Please try again.");
        setToastType("error");
      }

    } catch (error) {
      //console.error("[AIChatBox] Error during stem separation process:", error);
      Sentry.captureException(error);

      let errorMessage;
      if (error.response?.data) {
        errorMessage = error.response.data.message || error.response.data.details;
      } else if (error instanceof TypeError) {
        errorMessage = "Network error. Please check your connection.";
      } else {
        errorMessage = error.message || "An unexpected error occurred during stem separation.";
      }

      setToastMessage(errorMessage);
      setToastType("error");
    } finally {
      setIsGeneratingStems(false);
      setTimeout(() => {
        if (toastType === "info") {
          setToastMessage(null);
        }
      }, 5000);
    }
  }, [currentResponse, selectedTracks, getCookie, setIsGeneratingStems, setStemTrackName, setToastMessage, setToastType, setStemProgress, posthog]);

  // Update the handleEyeClick function
  // --- EDIT 2: Modify handleEyeClick to use selected track ---
  // ... existing code ...
  const handleEyeClick = () => {
    posthog.capture("AI Chatbox Preview Click");

    const currentlySelectedTrackIndex = Object.keys(selectedTracks).find(index => selectedTracks[index] === true);

    if (currentlySelectedTrackIndex === undefined) {
      setToastMessage("Please select a track version to preview.");
      setToastType("info");
      return;
    }

    const numericTrackIndex = parseInt(currentlySelectedTrackIndex, 10);

    // --- Check if the selected track for preview is in an error state ---
    if (trackStatuses[numericTrackIndex] === 'error') {
      setToastMessage("This track version encountered an error and cannot be previewed.");
      setToastType("error");
      return;
    }
    // --- End error check ---

    posthog.capture("Click-PreviewContent-ChatPage", {
      is_user_logged_in: true
    });

    // Add task completion for preview content
    (async () => {
      let taskCompleted = false;
      try {
        const previewTask = gamificationData?.tasks?.find(task => task.task_key === "preview_on_music");
        if (previewTask && !previewTask.is_completed) {
          console.log("Attempting to complete preview task");
          await completeTask("preview_on_music");
          console.log("Preview content task completed successfully");
          await fetchGamificationData();
          taskCompleted = true;
        } else {
          // console.log("Preview task already completed or not found", previewTask); 
        }
      } catch (error) {
        console.error("Error handling preview content task:", error);
        Sentry.captureException(error);
      } finally {
        if (taskCompleted && setTriggerTaskModal) {
          setTriggerTaskModal(true);
          setTimeout(() => setTriggerTaskModal(false), 5000);
        }
      }
    })();

    // Close details panel if it's open
    updateLocalState("isDetailsPanelOpen", false);

    // Update song details for the *selected* track before opening preview panel
    updateSongDetails(numericTrackIndex); // Pass the numeric index

    // The line below was redundant and likely causing the issue by calling updateSongDetails without an argument.
    // updateSongDetails(); 

    // Open the preview panel
    setTimeout(() => {
      updateLocalState("isPreviewPanelOpen", true);
      updateLocalState(
        "currentPlayingResponseId",
        responses[currentResponseIndex].ai_response_id // This ID remains the same for the message
      );
    }, 50);
  };
  // ... existing code ...
  // --- END EDIT 2 ---

  // Update the handleShare function
  const handleShare = async () => {
    posthog.capture("Click-ShareTrack-ChatPage", {
      is_user_logged_in: true
    });
    setIsSharing(true);
    setShareError(null);
    setGeneratedShareUrl(null);

    const currentResponse = responses[currentResponseIndex];
    if (!currentResponse || !currentResponse.ai_response_id) {
      setShareError("Cannot share this track (missing ID).");
      setIsSharing(false);
      return;
    }

    let taskCompleted = false;
    try {
      // First attempt to find and complete the share task
      try {
        const shareTask = gamificationData?.tasks?.find(task => task.task_key === "share_your_track");
        if (shareTask && !shareTask.is_completed) {
          console.log("Attempting to complete share task");
          await completeTask("share_your_track");
          console.log("Share track task completed successfully");
          await fetchGamificationData();
          taskCompleted = true;
        } else {
          console.log("Share task already completed or not found", shareTask);
        }
      } catch (taskError) {
        console.error("Error updating share track task:", taskError);
        Sentry.captureException(taskError);
      }

      // Then create the share link
      const response = await apiService.createShareLink(currentResponse.ai_response_id);
      if (response.success && response.shareUrl) {
        setGeneratedShareUrl(response.shareUrl);
        setIsShareModalOpen(true);
      } else {
        throw new Error(response.message || "Failed to create share link.");
      }
    } catch (error) {
      console.error("Share error:", error);
      Sentry.captureException(error);
      setShareError(error.message || "Could not create share link.");
      setToastMessage(error.message || "Could not create share link.");
      setToastType("error");
    } finally {
      setIsSharing(false);
      if (taskCompleted && setTriggerTaskModal) {
        setTriggerTaskModal(true);
        setTimeout(() => setTriggerTaskModal(false), 5000);
      }
    }
  };

  // Add a handler function for regenerating the What's next suggestions
  // const handleRegenerateSuggestions = () => {
  //   posthog.capture("AI Chatbox Regenerate Suggestions Click");
  //   setSuggestionsLoading(true);

  //   // Simulate loading time (in a real implementation, you would fetch new suggestions)
  //   setTimeout(() => {
  //     setSuggestionsLoading(false);
  //   }, 2000);
  // };

  // Helper function to safely get suggestions
  const getSuggestions = () => {
    const suggestions = localState.nextPromptSuggestion;

    // Add debug logging
    //console.log('Raw suggestions from localState:', localState.nextPromptSuggestion);
    //console.log('Processed suggestions:', suggestions);

    if (!suggestions) return null;

    try {
      // If it's already an array, return it directly
      if (Array.isArray(suggestions)) {
        return suggestions;
      }

      // If it's a string that looks like JSON, try to parse it
      if (typeof suggestions === 'string') {
        // First try to parse it as JSON
        try {
          const parsed = JSON.parse(suggestions);
          if (Array.isArray(parsed)) {
            return parsed;
          }
          if (parsed.suggestions && Array.isArray(parsed.suggestions)) {
            return parsed.suggestions;
          }
        } catch (e) {
          // Sentry.captureException(e);
          // If JSON parsing fails, split by comma and clean up each suggestion
          return suggestions
            .split(',')
            .map(s => s.trim())
            .map(s => s.replace(/[\[\]"{}]/g, '')) // Remove any JSON artifacts
            .filter(s => s); // Remove empty strings
        }
      }

      // If it's an object with a suggestions array
      if (typeof suggestions === 'object' && suggestions !== null) {
        if (suggestions.suggestions && Array.isArray(suggestions.suggestions)) {
          return suggestions.suggestions;
        }
        if (suggestions.text) {
          return suggestions.text
            .split(',')
            .map(s => s.trim())
            .map(s => s.replace(/[\[\]"{}]/g, '')) // Remove any JSON artifacts
            .filter(s => s);
        }
      }

      return null;
    } catch (error) {
      console.error('Error parsing suggestions:', error);
      Sentry.captureException(error);
      return null;
    }
  };

  // Handler for checkbox change
  const handleCheckboxChange = async (trackIndex) => { // Make async

    posthog.capture("Click-SelectTrack-AIChatbox-ChatPage", {
      is_user_logged_in: true,
      trackIndex, // 0, 1, or 2
      isLastMessage, // Helpful to see how often people try invalid clicks
      ai_response_id: currentResponse?.ai_response_id ?? null,
      
    });
    // Prevent changing selection if this is not the last message
    if (!isLastMessage) {
      setToastMessage("You can only select tracks from the latest response.");
      setToastType('info');
      return;
    }

    const isCurrentlySelected = selectedTracks[trackIndex];
    const responseId = currentResponse.ai_response_id; // Get the response ID

    updateLocalState("trackSelections", {
      ...localState.trackSelections,
      [responseId]: trackIndex
    });

    // Check if attempting to deselect a liked/disliked track (only applies if it IS the last message)
    if (isCurrentlySelected) {
      posthog.capture("Click-UnselectTrack-AIChatbox-ChatPage", {
        is_user_logged_in: true,
        trackIndex,
        ai_response_id: responseId,
        
      });
      const currentLikeStatus = likeStatuses[responseId];
      if (currentLikeStatus === 'like' || currentLikeStatus === 'dislike') {
        setToastMessage(`Please ${currentLikeStatus === 'like' ? 'unlike' : 'remove dislike for'} the track before deselecting.`);
        setToastType('info');
        return; // Prevent deselection
      }
    }

    // Determine the next state and the trackId to send to backend
    const newSelectedState = !isCurrentlySelected;
    const trackIdToSend = newSelectedState ? trackIndex : null; // Send null if deselecting

    // --- EDIT 1: Call backend API ---
    try {
      //console.log(`[AIChatBox] Updating selected track. ResponseID: ${responseId}, TrackIndexToSend: ${trackIdToSend}`);
      await apiService.updateSelectedTrack({ // Or use direct fetch/axios
        responseId: responseId,
        trackId: trackIdToSend
      });
      //console.log(`[AIChatBox] Backend update successful.`);

      // --- If backend update is successful, THEN update frontend state ---

      // Proceed with frontend selection/deselection logic
      const newSelectedTracks = {
        0: trackIndex === 0 ? newSelectedState : false,
        1: trackIndex === 1 ? newSelectedState : false,
        2: trackIndex === 2 ? newSelectedState : false,
      };
      setSelectedTracks(newSelectedTracks);

      // Update localState with details object or null
      const isNowSelected = newSelectedTracks[0] || newSelectedTracks[1] || newSelectedTracks[2];
      // Pass the actual selected index (0, 1, or 2) or null if none are selected
      const selectedLocalTrackIndex = isNowSelected ? Object.keys(newSelectedTracks).find(idx => newSelectedTracks[idx]) : null;
      updateLocalState('selectedTrackDetails', isNowSelected ? { responseId: responseId, trackIndex: parseInt(selectedLocalTrackIndex, 10) } : null);

      // PostHog event
      posthog.capture("Track Selection Changed", {
        is_user_logged_in: true,
        trackIndex: trackIndex,
        selected: newSelectedState,
        ai_response_id: responseId,
       
      });
      // --- End frontend state update ---

    } catch (error) {
      console.error("Error updating selected track in backend:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to update track selection. Please try again.");
      setToastType("error");
      // Optional: Revert frontend state if backend failed? (More complex)
    }
    // --- END EDIT 1 ---
  };

  // Determine if any track is selected and which one
  const selectedTrackIndex = Object.keys(selectedTracks).find(index => selectedTracks[index] === true); // Find the index ('0', '1', or '2')
  const isAnyTrackSelected = !!selectedTrackIndex; // Boolean check remains the same

  // --- EDIT 41: Add log before return ---
  //console.log(`[AIChatBox Render ${message.ai_response_id}] Rendering with trackStatuses:`, trackStatuses);
  // --- END EDIT 41 ---

  // --- EDIT 3: Add useEffect to initialize selectedTracks from currentResponse ---
  useEffect(() => {
    // This log is crucial
    if (currentResponse) {
      //console.log(`[AIChatBox ${currentResponse.ai_response_id}] useEffect for selectedTracks - currentResponse.selected_track_id:`, currentResponse.selected_track_id);
    }

    if (currentResponse && currentResponse.selected_track_id !== null && currentResponse.selected_track_id !== undefined) {
      const selectedIndex = parseInt(currentResponse.selected_track_id, 10);
      // This log confirms initialization attempt
      //console.log(`[AIChatBox ${currentResponse.ai_response_id}] Initializing selectedTracks from currentResponse.selected_track_id: ${selectedIndex}`);
      if (!isNaN(selectedIndex) && selectedIndex >= 0 && selectedIndex < (message.trackCount || 3)) {
        setSelectedTracks({
          0: selectedIndex === 0,
          1: selectedIndex === 1,
          2: selectedIndex === 2,
        });
      } else {
        //console.warn(`[AIChatBox ${currentResponse.ai_response_id}] selected_track_id ${selectedIndex} is invalid or out of bounds. Resetting selection.`);
        setSelectedTracks({ 0: false, 1: false, 2: false });
      }
    } else if (currentResponse) {
      // console.log(`[AIChatBox ${currentResponse.ai_response_id}] No selected_track_id in currentResponse or it's null. Ensuring selection is reset if needed.`);
      // Consider if you need to explicitly reset here or if the default state is sufficient.
      // If selectedTracks is already {0:false, 1:false, 2:false}, this might be redundant or cause an extra render.
    }
  }, [currentResponse, message.trackCount]); // Add message.trackCount as dependency
  // --- END EDIT 3 ---

  // --- START OF ADDED CODE ---
  // Effect to synchronize AudioPlayer's spinner with individual track streaming status
  useEffect(() => {
    // If this AIChatBox instance believes one of its tracks is playing (playingTrackIndex is set)
    // AND the global AudioPlayer context agrees (contextAiResponseId matches this box's currentResponse)
    if (playingTrackIndex !== null && currentResponse && contextAiResponseId === currentResponse.ai_response_id) {
      const responseId = currentResponse.ai_response_id;
      const requestId = currentResponse.requestId || responseId;
      // Construct the stream URL for the track this AIChatBox instance thinks is playing
      const expectedStreamUrlForPlayingTrack = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${requestId}/${playingTrackIndex}`;

      // Check if the AudioPlayer is indeed playing this specific stream URL
      if (currentAudioSrcFromContext === expectedStreamUrlForPlayingTrack) {
        const statusOfThisTrack = trackStatuses[playingTrackIndex];

        if ((statusOfThisTrack === 'complete' || statusOfThisTrack === 'error')) {
          // If this track was the one being buffered globally, clear the global buffering state
          if (isBufferingFromContext && streamingSrcFromContext === expectedStreamUrlForPlayingTrack) {
            // console.log(`[AIChatBox SyncSpinner] Track ${playingTrackIndex} of ${responseId} (src: ${expectedStreamUrlForPlayingTrack}) finished streaming (${statusOfThisTrack}). Clearing AudioPlayer buffer.`);
            setIsBuffering(false);
            // setStreamingSrc(null); // Optional: Clearing isBuffering is primary for spinner. AudioPlayer might handle src clearing.
          }
        } else if (statusOfThisTrack === 'streaming') {
          // If this track is streaming and is the one playing, ensure global buffering is on.
          // This is a safeguard, as handleStreamingAudio should set this when play starts.
          if (!isBufferingFromContext || streamingSrcFromContext !== expectedStreamUrlForPlayingTrack) {
            // console.log(`[AIChatBox SyncSpinner] Track ${playingTrackIndex} of ${responseId} (src: ${expectedStreamUrlForPlayingTrack}) is streaming. Ensuring AudioPlayer buffer ON.`);
            setIsBuffering(true);
            setStreamingSrc(expectedStreamUrlForPlayingTrack); // Ensure streamingSrc is also set for AudioPlayer's check
          }
        }
      }
    }
  }, [
    trackStatuses,
    playingTrackIndex,
    currentResponse,
    contextAiResponseId,
    currentAudioSrcFromContext,
    isBufferingFromContext,
    streamingSrcFromContext,
    setIsBuffering,
    setStreamingSrc,
  ]);

  // Auto-play effect: Start playing the first available streaming track
  useEffect(() => {
    if (!currentResponse || !isLastMessage) return;
    
    // Reset auto-play flag when response changes
    if (currentResponse.ai_response_id !== prevResponseIdRef.current) {
      setHasAutoPlayed(false);
      prevResponseIdRef.current = currentResponse.ai_response_id;
    }
    
    // Don't auto-play if we already did for this response
    if (hasAutoPlayed) return;
    
    // Don't auto-play if something else is already playing from a different response
    if (isPlaying && contextAiResponseId && contextAiResponseId !== currentResponse.ai_response_id) return;
    
    // Find the first track that is now streaming and auto-play it
    for (let i = 0; i < 3; i++) {
      if (trackStatuses[i] === 'streaming') {
        console.log(`[AIChatBox Auto-play] Track ${i} is now streaming for response ${currentResponse.ai_response_id}, starting auto-play`);
        setHasAutoPlayed(true);
        // Use a small delay to ensure the streaming is ready
        setTimeout(() => {
          handlePlayPause(i, false);
        }, 100);
        break; // Only auto-play the first available track
      }
    }
     }, [trackStatuses, currentResponse, isLastMessage, isPlaying, contextAiResponseId, handlePlayPause, hasAutoPlayed]);
  // --- END OF ADDED CODE ---

  // --- START OF NEW EFFECT TO HANDLE PLAY REQUESTS FROM VERSION HISTORY ---
  useEffect(() => {
    if (
      localState.playRequestFromHistory &&
      currentResponse &&
      localState.playRequestFromHistory.responseId === currentResponse.ai_response_id
    ) {
      const { trackIndex, timestamp } = localState.playRequestFromHistory; // Added timestamp for logging
      console.log(`[AIChatBox PlayRequestEffect ${currentResponse.ai_response_id}/${trackIndex}] Received playRequestFromHistory (timestamp: ${timestamp}). Context isPlaying: ${isPlaying}, Context audioSrc: ${currentAudioSrcFromContext}. Calling handlePlayPause.`);

      handlePlayPause(trackIndex);

      console.log(`[AIChatBox PlayRequestEffect ${currentResponse.ai_response_id}/${trackIndex}] Consumed playRequestFromHistory by setting to null.`);
      updateLocalState("playRequestFromHistory", null);
    }
  }, [
    localState.playRequestFromHistory,
    currentResponse,
    handlePlayPause,
    updateLocalState,
    isPlaying, // Added for logging context
    currentAudioSrcFromContext // Added for logging context
  ]);
  // --- END OF NEW EFFECT ---

  // --- START OF EFFECT TO HANDLE AUTOPLAY REQUESTS FROM AUDIOPLAYER ---
  useEffect(() => {
    if (
      localState.userRequestedAutoplay?.enabled &&
      isLastMessage &&
      currentResponse &&
      currentResponse.audio &&
      areAllTracksComplete(trackStatuses)
    ) {
      console.log(`[AIChatBox AutoplayRequest ${currentResponse.ai_response_id}] User requested autoplay from AudioPlayer. Starting autoplay sequence.`);
      
      // Start autoplay from track 0
      if (!isAutoplaying && currentAutoplayTrackIndex === null) {
        setIsAutoplaying(true);
        setCurrentAutoplayTrackIndex(0);
        setAutoplayInitiatedForThisMessage(true);
        autoplaySetupForResponseIdRef.current = currentResponse.ai_response_id;
        
        // Play the first track
        handlePlayPause(0, true); // true = triggered by autoplay
        
        console.log(`[AIChatBox AutoplayRequest ${currentResponse.ai_response_id}] Autoplay initiated for track 0`);
      }
      
      // Clear the request after processing
      updateLocalState("userRequestedAutoplay", null);
    }
  }, [
    localState.userRequestedAutoplay,
    isLastMessage,
    currentResponse,
    areAllTracksComplete,
    trackStatuses,
    isAutoplaying,
    currentAutoplayTrackIndex,
    handlePlayPause,
    updateLocalState
  ]);
  // --- END OF EFFECT TO HANDLE AUTOPLAY REQUESTS ---

  // --- START OF EFFECT TO HANDLE AUTOPLAY DISABLE FROM AUDIOPLAYER ---
  useEffect(() => {
    // If AudioPlayer sets userAutoplayPreference to false (either directly or via repeat enable),
    // stop any active autoplay in this AIChatBox
    if (localState.userAutoplayPreference === false && isAutoplaying && currentResponse) {
      console.log(`[AIChatBox AutoplayDisable ${currentResponse.ai_response_id}] AudioPlayer disabled autoplay. Stopping autoplay sequence.`);
      setIsAutoplaying(false);
      setCurrentAutoplayTrackIndex(null);
      setAutoplayInitiatedForThisMessage(false);
      autoplaySetupForResponseIdRef.current = null;
      
      // Note: We don't stop the currently playing track, just the autoplay sequence
      // This allows repeat mode to take over seamlessly
    }
  }, [
    localState.userAutoplayPreference,
    isAutoplaying,
    currentResponse,
    setIsAutoplaying,
    setCurrentAutoplayTrackIndex,
    setAutoplayInitiatedForThisMessage
  ]);
  // --- END OF EFFECT TO HANDLE AUTOPLAY DISABLE FROM AUDIOPLAYER ---

  // Autoplay Initiation Effect
  useEffect(() => {
    // --- ADDED: stopGeneration check ---
    if (localState.stopGeneration) {
      // If generation is stopped, don't initiate autoplay, even if conditions were met.
      // Also, ensure any ongoing autoplay for this message is stopped.
      if (isAutoplaying && currentResponse && contextAiResponseId === currentResponse.ai_response_id) {
        console.log(`[AIChatBox Autoplay Init ${currentResponse.ai_response_id}] stopGeneration is true. Turning off active autoplay.`);
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
        if (isPlaying && currentAudioSrcFromContext && currentAudioSrcFromContext.includes(currentResponse.requestId || currentResponse.ai_response_id)) {
          stopAudio();
        }
      }
      return;
    }
    // --- END: stopGeneration check ---

    if (!currentResponse || !currentResponse?.trackCount || currentResponse?.trackCount === 0) return;


    const responseId = currentResponse.ai_response_id;
    const isTargetLoadingMessage = loadingResponseId === responseId;
    // --- EDIT 5: Add specific log for 3rd regeneration debugging ---
    console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Running. loadingResponseId: ${loadingResponseId}, isTargetLoading: ${isTargetLoadingMessage}, autoplayInitiatedState: ${autoplayInitiatedForThisMessage}, autoplaySetupForRef: ${autoplaySetupForResponseIdRef.current}, isAutoplaying: ${isAutoplaying}, currentAutoplayTrackIndex: ${currentAutoplayTrackIndex}, trackStatuses[0]: ${trackStatuses ? trackStatuses[0] : 'N/A'}`);
    // --- END EDIT 5 ---

    // --- EDIT 2: Reset autoplay initiation state if loading a new response ID that hasn't had autoplay setup ---
    if (isTargetLoadingMessage && autoplaySetupForResponseIdRef.current !== responseId && autoplayInitiatedForThisMessage) {
      console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] New target or ref mismatch. Resetting autoplayInitiatedForThisMessage from true to false. Ref: ${autoplaySetupForResponseIdRef.current}, loadingId: ${loadingResponseId}`);
      setAutoplayInitiatedForThisMessage(false);
      // It's crucial to also reset isAutoplaying and currentAutoplayTrackIndex here if we are resetting the initiation flag
      // because the main condition below relies on them being in a "ready to start" state.
      // The click handlers already do this, but this ensures consistency if this path is taken.
      if (isAutoplaying || currentAutoplayTrackIndex !== null) {
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
      }
      // After this state change, the effect will re-run.
      return;
    }
    // --- END EDIT 2 ---

    // Only initiate if autoplay is not already active for this message AND currentAutoplayTrackIndex is not already set
    if (isTargetLoadingMessage && !autoplayInitiatedForThisMessage && !isAutoplaying && currentAutoplayTrackIndex === null) {
      // --- EDIT 1: Refined logic to prevent replay of completed tracks after regenerate click ---
      let allowAutoplaySetup = false;
      const statusOfTrackZero = trackStatuses ? trackStatuses[0] : undefined;

      if (statusOfTrackZero === 'streaming' || statusOfTrackZero === 'pending') {
        allowAutoplaySetup = true;
        console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Eligible for autoplay (pending/streaming).`);
      } else if (statusOfTrackZero === 'complete') {
        if (autoplaySetupForResponseIdRef.current === null) {
          // This means a regenerate button was just clicked (ref was set to null).
          // Don't restart autoplay for the old 'complete' track immediately.
          allowAutoplaySetup = false;
          console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Autoplay for COMPLETED track SKIPPED: autoplaySetupForResponseIdRef is null (regeneration click).`);
        } else if (autoplaySetupForResponseIdRef.current !== responseId) {
          // This is for an initial load where the target is already complete, 
          // and we haven't set up this specific responseId in the ref yet.
          allowAutoplaySetup = true;
          console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Eligible for autoplay (complete, new ID for ref). Ref: ${autoplaySetupForResponseIdRef.current}`);
        } else {
          // Track is complete, and ref already matches responseId. Autoplay already handled or finished for this version.
          allowAutoplaySetup = false;
          console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Autoplay for COMPLETED track SKIPPED: ref (${autoplaySetupForResponseIdRef.current}) matches responseId, already set up for this version.`);
        }
      }

      if (allowAutoplaySetup) {
        console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] CONDITIONS MET. Setting up autoplay for track 0 (status: ${statusOfTrackZero}).`);
        // --- END EDIT 6 ---
        setIsAutoplaying(true);
        setCurrentAutoplayTrackIndex(0); // Set target to track 0
        setAutoplayInitiatedForThisMessage(true);
        autoplaySetupForResponseIdRef.current = responseId; // Mark that setup has been done for this responseId
        // The Advancement effect will now attempt to play track 0 if ready.
      } else if (statusOfTrackZero !== 'error' && statusOfTrackZero !== undefined) { // Avoid excessive logging for non-actionable states
        // Log only if not already logged by specific skip conditions for 'complete'
        if (!(statusOfTrackZero === 'complete' && (autoplaySetupForResponseIdRef.current === null || autoplaySetupForResponseIdRef.current === responseId))) {
          console.log(`[AIChatBox AutoplayInit थ्री ${responseId}] Autoplay setup NOT ALLOWED based on combined conditions. isTargetLoading: ${isTargetLoadingMessage}, !autoplayInitiated: ${!autoplayInitiatedForThisMessage}, !isAutoplaying: ${!isAutoplaying}, currentAutoplayIdx: ${currentAutoplayTrackIndex}, trackStatus[0]: ${statusOfTrackZero}, ref: ${autoplaySetupForResponseIdRef.current}`);
        }
      }
      // --- END EDIT 1 ---
    }
  }, [
    // Key states that drive the logic:
    localState.stopGeneration,
    isAutoplaying,
    currentResponse,
    contextAiResponseId, // For stopGeneration block
    setIsAutoplaying,
    setCurrentAutoplayTrackIndex,
    isPlaying, // For stopGeneration block
    currentAudioSrcFromContext, // For stopGeneration block
    stopAudio, // For stopGeneration block

    loadingResponseId,
    trackStatuses,
    autoplayInitiatedForThisMessage, // Still a dependency because we read and set it
    setAutoplayInitiatedForThisMessage // Dependency for setter
    // autoplaySetupForResponseIdRef is a ref, so not in dependencies
  ]);

  // Autoplay Advancement Effect
  useEffect(() => {
    const responseId = currentResponse ? currentResponse.ai_response_id : "N/A";
    const localPlayingTrackIdx = playingTrackIndex; // Capture for consistent use in this run

    // --- EDIT 2: Enhanced logging for Autoplay Advancement ---
    console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] EFFECT RUN. isAutoplaying: ${isAutoplaying}, currentAutoplayTargetIdx: ${currentAutoplayTrackIndex}, isPlaying(ctx): ${isPlaying}, ctxAiResponseId: ${contextAiResponseId}, ctxSrc: ${currentAudioSrcFromContext}, CT: ${currentTime}, D: ${duration}, localPlayingTrackIdx: ${localPlayingTrackIdx}, trackStatuses:`, JSON.stringify(trackStatuses), localState);
    // --- END EDIT 2 ---

    // Values from the previous render cycle (before current isPlaying, currentAudioSrcFromContext were updated for this render)
    const wasPlayingInPreviousCycle = prevIsPlaying.current;
    const previousContextSrc = prevAudioSrc.current;

    // --- ADDED: stopGeneration check ---
    if (localState.stopGeneration) {
      if (isAutoplaying) { // Only act if autoplay was even on
        console.log(`[AIChatBox Autoplay ADV ${responseId}] stopGeneration is true. Turning off autoplay.`);
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
        // If this message's audio was playing due to autoplay, stop it.
        if (isPlaying && contextAiResponseId === responseId && currentAudioSrcFromContext && currentResponse && currentAudioSrcFromContext.includes(currentResponse.requestId || responseId)) {
          stopAudio();
        }
      }
      // Update refs before returning, as the rest of the effect is skipped
      prevIsPlaying.current = isPlaying;
      prevAudioSrc.current = currentAudioSrcFromContext;
      return;
    }
    // --- END: stopGeneration check ---

    //console.log(`[AIChatBox Autoplay ADV ${responseId}] EFFECT RUN. isAutoplaying: ${isAutoplaying}, currentAutoplayTargetIdx: ${currentAutoplayTrackIndex}, isPlaying(ctx): ${isPlaying}, ctxAiResponseId: ${contextAiResponseId}, ctxSrc: ${currentAudioSrcFromContext}, CT: ${currentTime}, D: ${duration}, localPlayingTrackIdx: ${playingTrackIndex}`);


    if (!isAutoplaying || !currentResponse) {
      if (isAutoplaying && !currentResponse) {
        console.log(`[AIChatBox AutoplayADV ${responseId}] Autoplay active but no currentResponse. Turning off autoplay.`);
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
      }
      // Update refs before returning if autoplay is off
      prevIsPlaying.current = isPlaying;
      prevAudioSrc.current = currentAudioSrcFromContext;
      return;
    }

    const trackCount = currentResponse.trackCount || 3;

    if (currentAutoplayTrackIndex === null || currentAutoplayTrackIndex >= trackCount) {
      if (currentAutoplayTrackIndex !== null) {
        console.log(`[AIChatBox AutoplayADV ${responseId}] Sequence ended: target index ${currentAutoplayTrackIndex} is invalid. Turning off autoplay.`);
      } else if (isAutoplaying) {
        console.log(`[AIChatBox AutoplayADV ${responseId}] Autoplay is true but currentAutoplayTrackIndex is null. Turning off autoplay.`);
      }
      setIsAutoplaying(false);
      setCurrentAutoplayTrackIndex(null);
      prevIsPlaying.current = isPlaying;
      prevAudioSrc.current = currentAudioSrcFromContext;
      return;
    }

    const isOurMessagePlayingInContext = contextAiResponseId === responseId;
    // Check if the track *currently targeted by autoplay* is the one playing in the global context
    // --- EDIT 1: Modify to check both chunk stream and GCS stream URLs ---
    const isAutoplayTargetTrackPlayingInContext =
      isPlaying &&
      isOurMessagePlayingInContext &&
      currentAudioSrcFromContext &&
      (currentAudioSrcFromContext.includes(`/api/audio-stream/${currentResponse.requestId || responseId}/${currentAutoplayTrackIndex}`) ||
        currentAudioSrcFromContext.includes(`/api/stream-audio/${responseId}/${currentAutoplayTrackIndex}`));
    // --- END EDIT 1 ---

    //console.log(`[AIChatBox AutoplayADV ${responseId}] currentAutoplayTargetIdx: ${currentAutoplayTrackIndex}, isAutoplayTargetTrackPlayingInContext: ${isAutoplayTargetTrackPlayingInContext}`);

    // --- Path 1: Player just stopped ---
    if (wasPlayingInPreviousCycle && !isPlaying) {
      // --- EDIT 3: Enhanced logging ---
      console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 1: Player JUST STOPPED. wasPlaying: ${wasPlayingInPreviousCycle}, isPlaying(ctx): ${isPlaying}. previousContextSrc: ${previousContextSrc}, currentContextSrc: ${currentAudioSrcFromContext}, aimedMessage: ${isOurMessagePlayingInContext}, aimedTrackWhenStopped: ${wasOurAutoplayTargetPlayingWhenItStopped}`);
      // --- END EDIT 3 ---

      // Check if the track that *was* playing (before stopping) was our current autoplay target
      const wasOurAutoplayTargetPlayingWhenItStopped =
        isOurMessagePlayingInContext && // It stopped for our message
        previousContextSrc && // Make sure there was a previous source
        // --- EDIT 1: Check against GCS stream URL for completed tracks as well during autoplay advancement ---
        (previousContextSrc.includes(`/api/audio-stream/${currentResponse.requestId || responseId}/${currentAutoplayTrackIndex}`) ||
          previousContextSrc.includes(`/api/stream-audio/${responseId}/${currentAutoplayTrackIndex}`));
      // --- END EDIT 1 ---

      if (wasOurAutoplayTargetPlayingWhenItStopped) {
        // --- EDIT 4: Enhanced logging ---
        console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 1: Our autoplay target track ${currentAutoplayTrackIndex} stopped. NaturallyEnded: ${trackNaturallyEnded} (CT: ${currentTime}, D: ${duration})`);
        // --- END EDIT 4 ---
        const trackNaturallyEnded = duration > 0 && (currentTime >= duration - 0.3 || currentTime >= duration);

        if (trackNaturallyEnded) {
          const nextTrack = currentAutoplayTrackIndex + 1;
          if (nextTrack < trackCount) {
            // --- EDIT 5: Enhanced logging ---
            console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 1: Advancing from ${currentAutoplayTrackIndex} to track ${nextTrack}. Status of next: ${trackStatuses ? trackStatuses[nextTrack] : 'N/A'}`);
            // --- END EDIT 5 ---
            setCurrentAutoplayTrackIndex(nextTrack);
            // isAutoplaying remains true to continue the sequence
          } else {
            console.log(`[AIChatBox AutoplayADV ${responseId}] Path 1: Autoplay sequence complete (all tracks played). Turning off autoplay.`);
            setIsAutoplaying(false);
            setCurrentAutoplayTrackIndex(null);
          }
        } else {
          // Track was paused by user (globally) or stopped unexpectedly before natural end.
          // Only turn off autoplay if it wasn't the natural end of the *last* track.
          // If it's an interruption, always stop.
          console.log(`[AIChatBox AutoplayADV ${responseId}] Path 1: Autoplay for track ${currentAutoplayTrackIndex} INTERRUPTED (not natural end). Setting isAutoplaying = false.`);
          setIsAutoplaying(false);
          setCurrentAutoplayTrackIndex(null);
        }
      } else if (isAutoplaying) {
        // Player stopped, but it wasn't our specific autoplay target for this message, or it was a different message.
        console.log(`[AIChatBox AutoplayADV ${responseId}] Path 1: Player stopped, but not our aimed track ${currentAutoplayTrackIndex} OR different message. contextId: ${contextAiResponseId}, prevSrc: ${previousContextSrc}. Stopping this box's autoplay.`);
        setIsAutoplaying(false);
        setCurrentAutoplayTrackIndex(null);
      }
      // Update refs and return to process state changes
      prevIsPlaying.current = isPlaying;
      prevAudioSrc.current = currentAudioSrcFromContext;
      return;
    }

    // --- Path 2: Attempt to play/resume the currentAutoplayTrackIndex if it's NOT already playing in context ---
    if (!isAutoplayTargetTrackPlayingInContext && isAutoplaying && currentAutoplayTrackIndex !== null) {
      const playerIsGenerallyIdleOrForDifferentMessage = !isPlaying || (contextAiResponseId !== responseId);
      // --- EDIT 6: Enhanced logging ---
      console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 2: Target track ${currentAutoplayTrackIndex} (status: ${trackStatuses ? trackStatuses[currentAutoplayTrackIndex] : 'N/A'}) NOT playing in context. playerIsGenerallyIdleOrForDifferentMessage: ${playerIsGenerallyIdleOrForDifferentMessage}.`);
      // --- END EDIT 6 ---

      if (playerIsGenerallyIdleOrForDifferentMessage) {
        if (trackStatuses[currentAutoplayTrackIndex] === 'streaming' || trackStatuses[currentAutoplayTrackIndex] === 'complete') {
          // --- EDIT 7: Enhanced logging ---
          console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 2: CALLING handlePlayPause(${currentAutoplayTrackIndex}, true) for ready track (status: ${trackStatuses[currentAutoplayTrackIndex]}).`);
          // --- END EDIT 7 ---
          handlePlayPause(currentAutoplayTrackIndex, true);
        } else if (trackStatuses[currentAutoplayTrackIndex] === 'pending') {
          // --- EDIT 8: Enhanced logging ---
          console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 2: Target track ${currentAutoplayTrackIndex} is 'pending'. Waiting for status update.`);
          // --- END EDIT 8 ---
        } else if (trackStatuses[currentAutoplayTrackIndex] === 'error') {
          // --- EDIT 9: Enhanced logging ---
          console.log(`[AIChatBox AutoplayADV थ्री ${responseId}] Path 2: Target track ${currentAutoplayTrackIndex} has 'error' status. Advancing or stopping autoplay.`);
          // --- END EDIT 9 ---
          const nextTrack = currentAutoplayTrackIndex + 1;
          if (nextTrack < trackCount) {
            setCurrentAutoplayTrackIndex(nextTrack);
          } else {
            setIsAutoplaying(false);
            setCurrentAutoplayTrackIndex(null);
          }
        }
      } else {
        // Player is busy with THIS message (contextAiResponseId === responseId), but it's NOT our autoplay target track.
        // This means the user likely clicked another track's play button within this AIChatBox.
        // `handlePlayPause` (called with isTriggeredByAutoplay=false) should have already stopped autoplay.
        // This is a safeguard.
        if (isAutoplaying) { // This check is important
          console.log(`[AIChatBox AutoplayADV ${responseId}] Path 2: Player busy with another track from THIS message (NOT target ${currentAutoplayTrackIndex}). Current global src: ${currentAudioSrcFromContext}. Stopping this box's autoplay.`);
          setIsAutoplaying(false);
          setCurrentAutoplayTrackIndex(null);
        }
      }
    }

    // Update refs at the end of the effect for the next run
    // console.log(`[AIChatBox AutoplayADV ${responseId}] End of effect. Updating prevIsPlaying to ${isPlaying}, prevAudioSrc to ${currentAudioSrcFromContext}`);
    prevIsPlaying.current = isPlaying;
    prevAudioSrc.current = currentAudioSrcFromContext;
  }, [
    // Key states that drive the logic:
    localState.stopGeneration, // Added
    isAutoplaying,
    currentAutoplayTrackIndex,
    currentResponse,
    trackStatuses,
    // AudioPlayer context values:
    isPlaying,
    contextAiResponseId,
    currentAudioSrcFromContext,
    currentTime,
    duration,
    playingTrackIndex, // Local UI state for which track is playing within this AIChatBox
    // Callbacks/Setters (should be stable or memoized):
    handlePlayPause,
    setIsAutoplaying,
    setCurrentAutoplayTrackIndex,
    stopAudio // Added for stopGeneration block
    // Note: prevIsPlaying and prevAudioSrc refs are NOT dependencies here.
  ]);

  // Effect to signal CustomInput if track selection is needed from this AIChatBox
  useEffect(() => {
    // --- MODIFIED: Prioritize currentResponse.isStopped ---
    if (currentResponse && currentResponse.isStopped) {
      // If this message instance is marked as stopped, it should not impose a track selection requirement.
      // Clear the requirement if it was previously set by this instance.
      if (localState.chatInputRequiresTrackSelectionForId === currentResponse.ai_response_id) {
        console.log(`[AIChatBox ${currentResponse.ai_response_id}] Message is stopped. Clearing chatInputRequiresTrackSelectionForId.`);
        updateLocalState("chatInputRequiresTrackSelectionForId", null);
      }
      return; // Exit: A stopped message doesn't need further evaluation for this logic.
    }
    // --- END MODIFICATION ---

    // Original logic proceeds if the message is not stopped:
    if (isLastMessage && currentResponse?.audio && !localState.responseInProgress) {
      if (!isAnyTrackSelected) {
        // This is the last message, has audio, nothing is selected from it, and no response is currently being generated for it.
        // Signal that input should be disabled until a selection is made from THIS specific response.
        console.log(`[AIChatBox ${currentResponse.ai_response_id}] Setting chatInputRequiresTrackSelectionForId to: ${currentResponse.ai_response_id}`);
        updateLocalState("chatInputRequiresTrackSelectionForId", currentResponse.ai_response_id);
      } else {
        // A track IS selected from this last message.
        if (localState.chatInputRequiresTrackSelectionForId === currentResponse.ai_response_id) {
          console.log(`[AIChatBox ${currentResponse.ai_response_id}] Clearing chatInputRequiresTrackSelectionForId because a track is now selected.`);
          updateLocalState("chatInputRequiresTrackSelectionForId", null);
        }
      }
    } else if (localState.chatInputRequiresTrackSelectionForId === currentResponse?.ai_response_id) {
      // This AIChatBox is no longer the one requiring selection (e.g., it's not the last message, 
      // or a new response started for it, or it's not the one that was stopped).
      console.log(`[AIChatBox ${currentResponse?.ai_response_id}] No longer the primary message requiring selection. Clearing chatInputRequiresTrackSelectionForId.`);
      updateLocalState("chatInputRequiresTrackSelectionForId", null);
    }

    // Ensure cleanup if the component unmounts while it was the one requiring selection
    return () => {
      // The cleanup logic might need to be re-evaluated based on this new primary condition,
      // but the main effect should now handle the stopped state correctly.
      if (currentResponse && currentResponse.isStopped) {
        // If unmounting and was stopped, ensure the flag is clear if this instance set it.
        if (localState.chatInputRequiresTrackSelectionForId === currentResponse.ai_response_id) {
          // console.log(`[AIChatBox UNMOUNT ${currentResponse.ai_response_id}] Was stopped. Clearing chatInputRequiresTrackSelectionForId.`);
          // updateLocalState("chatInputRequiresTrackSelectionForId", null); // Potentially redundant if main effect ran.
        }
      } else if (localState.chatInputRequiresTrackSelectionForId === currentResponse?.ai_response_id) {
        // console.log(`[AIChatBox UNMOUNT ${currentResponse?.ai_response_id}] Clearing chatInputRequiresTrackSelectionForId.`);
        // updateLocalState("chatInputRequiresTrackSelectionForId", null); // Careful with unmount logic.
      }
    };
  }, [
    currentResponse?.isStopped, // Added: To react when this message is marked as stopped
    currentResponse?.ai_response_id, // Renamed from currentResponse.ai_response_id for clarity
    isLastMessage,
    currentResponse?.audio,
    isAnyTrackSelected,
    localState?.responseInProgress,
    localState?.chatInputRequiresTrackSelectionForId,

    updateLocalState
    // localState.stopGeneration and localState.ai_response_id (global stop indicators)
    // are not strictly needed here if currentResponse.isStopped is reliable.
  ]);


  const disableVersionToggle = isAnyTrackSelected || !isLastMessage;
  // Effect to synchronize AIChatBox's local playingTrackIndex with the global AudioPlayerContext
  useEffect(() => {
    const thisResponseId = currentResponse?.ai_response_id;
    if (!thisResponseId) {
      if (playingTrackIndex !== null) {
        // console.log(`[AIChatBox SyncEffect ${thisResponseId}] No currentResponse, clearing playingTrackIndex from ${playingTrackIndex}`);
        setPlayingTrackIndex(null);
      }
      return;
    }

    let newCalculatedPlayingTrackIndex = null;

    if (contextAiResponseId === thisResponseId && isPlaying) {
      // Context is playing this AI response. Check which track.
      const requestIdForComparison = currentResponse.requestId || thisResponseId;
      for (let idx = 0; idx < (currentResponse.trackCount || 3); idx++) {
        const gcsUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${thisResponseId}/${idx}`;
        const chunkUrl = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${requestIdForComparison}/${idx}`;
        if (currentAudioSrcFromContext === gcsUrl || currentAudioSrcFromContext === chunkUrl) {
          newCalculatedPlayingTrackIndex = idx;
          break;
        }
      }
    }
    // If newCalculatedPlayingTrackIndex is different from playingTrackIndex, update.
    if (playingTrackIndex !== newCalculatedPlayingTrackIndex) {
      console.log(`[AIChatBox SyncEffect ${thisResponseId}] Updating local playingTrackIndex from ${playingTrackIndex} to ${newCalculatedPlayingTrackIndex}. Context: isPlaying=${isPlaying}, contextSrc=${currentAudioSrcFromContext}, contextId=${contextAiResponseId}`);
      setPlayingTrackIndex(newCalculatedPlayingTrackIndex);
    }

  }, [
    isPlaying, // From AudioPlayerContext
    currentAudioSrcFromContext, // From AudioPlayerContext
    contextAiResponseId, // From AudioPlayerContext
    currentResponse, // From props, to get ai_response_id, requestId, trackCount
    playingTrackIndex, // Local state, to compare for changes
    // setPlayingTrackIndex is not a dependency as it's a setter
  ]);

  // --- START OF ADDED CODE ---
  // This effect ensures trackStatuses are correctly initialized/reset
  // when the currentResponse changes or when it becomes the loading target.
  // IMPORTANT: This effect should NOT interfere with polling-managed status transitions
  useEffect(() => {
    const responseToEvaluate = currentResponse; // The message version currently being displayed/focused

    if (!responseToEvaluate || !responseToEvaluate.ai_response_id) {
      return;
    }

    const activeId = responseToEvaluate.ai_response_id;
    const isThisResponseTheLoadingTarget = loadingResponseId === activeId;
    const expectedTrackCount = responseToEvaluate.trackCount || 3;

    console.log(`[AIChatBox TrackManagerEffect FIXED for ${activeId}] Running. IsLoadingTarget: ${isThisResponseTheLoadingTarget}, TrackCount: ${expectedTrackCount}, HasAudio: ${!!responseToEvaluate.audio}, IsError: ${!!responseToEvaluate.is_error}, loadingResponseId: ${loadingResponseId}`);

    setTrackStatuses(prevStatuses => {
      const newCalculatedStatuses = {};
      let statusesNeedChange = false;

      // Only recalculate if track count changed or this is a fundamentally different response
      const trackCountChanged = Object.keys(prevStatuses).length !== expectedTrackCount && expectedTrackCount > 0;
      
      for (let i = 0; i < expectedTrackCount; i++) {
        let newStatusForTrack;
        const currentStatus = prevStatuses[i];
        
        if (responseToEvaluate.is_error) {
          newStatusForTrack = 'error';
        } else if (isThisResponseTheLoadingTarget) {
          // CRITICAL FIX: Only reset to 'pending' if the current status is not already in a polling-managed transition
          // Don't override 'streaming' status - let the polling effect manage that
          if (currentStatus === 'streaming') {
            newStatusForTrack = currentStatus; // Keep existing 'streaming' status
          } else if (!currentStatus || currentStatus === 'complete') {
            newStatusForTrack = 'pending'; // Initialize or reset from complete to pending for new loading
          } else {
            newStatusForTrack = currentStatus; // Keep current status (pending, etc.)
          }
        } else if (responseToEvaluate.audio) {
          // Not the loading target, but has audio, so it's 'complete'.
          // Only change from non-complete statuses to complete
          if (currentStatus !== 'complete') {
            newStatusForTrack = 'complete';
          } else {
            newStatusForTrack = currentStatus;
          }
        } else {
          // Not loading, not error, no audio.
          newStatusForTrack = 'pending';
        }

        if (prevStatuses[i] !== newStatusForTrack || trackCountChanged) {
          statusesNeedChange = true;
        }
        newCalculatedStatuses[i] = newStatusForTrack;
      }

      if (statusesNeedChange) {
        console.log(`[AIChatBox TrackManagerEffect FIXED for ${activeId}] Updating statuses from`, prevStatuses, "to", newCalculatedStatuses);
        return newCalculatedStatuses;
      }
      return prevStatuses; // No change needed
    });

  }, [currentResponse, loadingResponseId]); // Key dependencies: the displayed message and the global loading ID.
  // --- END OF ADDED CODE ---

  // Mac detecter function to detect 
  function isMacUser() {
    if (typeof navigator !== 'undefined') {
      return /Mac/i.test(navigator.userAgent);
    }
    return false;
  }

  return (
    <div
      id={id}
      className={`mb-0 gap-0 flex flex-col w-full items-center max-w-screen-lg lg:max-w-screen-md relative`}

    >
      <div
        className={`flex items-start hover:[background:var(--Color-Opacity-Primary-10,rgba(186,24,236,0.10))] transition duration-500 p-3 mb-2 rounded-lg group relative w-full max-w-screen-lg lg:max-w-screen-md`}
        style={
          isHighlighted
            ? {
              borderRadius: "10px",
              background:
                "linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.72) 100%), linear-gradient(135deg, #FC10F2 0%, #3078E4 100%)",
            }
            : {}
        }
      >
        <img
          src="/logo.png"
          alt="AI Avatar"
          className="w-8 h-8 rounded-full bg-[#2c2a2c] p-1"
        />
        {currentResponse.isStopped ? (
          <div className="flex flex-col gap-2 px-3">
            <div className="flex p-2 w-full font-extralight max-w-screen-md flex-col gap-3 justify-center items-start">
              {currentResponse.text}
            </div>
          </div>
        ) : currentResponse.is_incomplete ? (
          <div className="flex flex-col gap-2 px-3 pt-1">
            <p className="text-base text-white font-extralight">
              {currentResponse.text}
            </p>
            {isLastMessage && (
              <button
                onClick={() => {
                  posthog.capture("Click-RegenerateTrack-AIChatbox-ChatPage", {
                    is_user_logged_in: true,
                    ai_response_id: currentResponse.ai_response_id,
                    request_id: currentResponse.requestId,
                  });
                  // --- EDIT 1: Stop audio and reset autoplay states before regeneration ---
                  if (isPlaying && contextAiResponseId === currentResponse.ai_response_id) {
                    console.log(`[AIChatBox REG थ्री INCOMPLETE ${currentResponse.ai_response_id}] Stopping audio before blank regeneration.`);
                    stopAudio();
                  }
                  setAutoplayInitiatedForThisMessage(false);
                  setIsAutoplaying(false);
                  setCurrentAutoplayTrackIndex(null);
                  autoplaySetupForResponseIdRef.current = null; // --- EDIT 3: Reset ref ---
                  // --- END EDIT 1 ---
                  onBlankRegenerate(
                    currentResponse.ai_response_id,
                    currentResponse.requestId
                  );
                }}
                className="flex w-24 p-1.5 rounded-lg gap-1 bg-white items-center border-none"
              >
                <ArrowsRotateRight className="w-3.5 h-3.5 text-black" />
                <span className="text-xs font-medium text-[#0f0e0f]">
                  Regenerate
                </span>
              </button>
            )}
          </div>
        ) : currentResponse?.is_error ? (
          <div className="flex flex-col gap-2 px-3">
            <div className="flex p-2 w-full max-w-screen-md flex-col gap-3 justify-center items-start self-stretch shrink-0 bg-[rgba(51,0,0,0.5)] rounded-lg border border-[rgba(153,30,30,0.4)] relative z-4">
              <div className="flex w-full gap-2 flex-wrap items-center text-sm font-light text-left z-8">
                <CircleExclamation className="text-[#FF5454]" />
                <span className="text-sm font-light text-[#fff]">
                  Something went wrong! Please try again or contact
                </span>
                <a href="mailto:support@wubble.ai">
                  <span className="text-sm font-light text-[#e181ff] break-words hover:underline cursor-pointer">
                    support@wubble.ai
                  </span>
                </a>
                <span className="text-sm font-light text-[#fff]">
                  if the issue persists.
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 inline-block min-w-0"> {/* Keep min-w-0 */}
            <div className="flex-col mt-1 mb-1 ml-3">
              <p
                className={`font-extralight text-base text-white ${currentResponse.audio ? "mb-3" : ""
                  }`}
              >
                {currentResponse.text}
              </p>
              {/* Outer container for tracks - keep w-full */}
              <div className="flex flex-col items-start w-full">
                {/* --- START: Track Rendering --- */}
                {/* Render track section if audio exists OR if it's currently loading */}
                {(currentResponse.audio || loadingResponseId === message.ai_response_id) && (
                  <>
                    {/* --- Track 1 --- */}
                    {(!isAnyTrackSelected || selectedTrackIndex === '0') && (
                      <div className="flex items-center gap-2 mb-2 max-w-md self-stretch">
                        {/* Checkbox Container 1 */}
                        {/* --- EDIT 1: Render container for streaming/complete/error, disable if streaming/not last --- */}
                        {currentResponse.audio && (trackStatuses[0] === 'streaming' || trackStatuses[0] === 'complete' || trackStatuses[0] === 'error') && (
                          <div
                            id="checkbox-container-1"
                            className={`
                              flex flex-shrink-0 w-8 h-8 px-2 justify-center items-center rounded-full bg-white/5 transition-colors duration-200
                              ${(!isLastMessage || trackStatuses[0] === 'streaming')
                                ? 'opacity-50 cursor-not-allowed' // Disabled styles
                                : 'hover:bg-white/10 cursor-pointer' // Enabled styles
                              }
                              ${selectedTracks[0] ? 'bg-white/10' : ''}
                            `}
                            onClick={() => {
                              // Prevent click if disabled
                              if (!isLastMessage || trackStatuses[0] === 'streaming') return;
                              handleCheckboxChange(0);
                            }}
                          >
                            {/* Render actual checkbox visual only when complete/error */}
                            {(trackStatuses[0] === 'complete' || trackStatuses[0] === 'error') && (
                              <div className={`w-4 h-4 flex-shrink-0 rounded-sm flex items-center justify-center ${selectedTracks[0] ? 'bg-white' : 'bg-transparent border border-white/30'}`}>
                                {selectedTracks[0] && <Check className="w-3 h-3 text-black" />}
                              </div>
                            )}
                            {/* Optional: Placeholder or different visual for streaming? For now, just empty */}
                          </div>
                        )}
                        {/* --- END EDIT 1 --- */}
                        {/* Spacer if checkbox container not rendered */}
                        {(!currentResponse.audio || trackStatuses[0] === 'pending') && message.trackCount > 1 && (
                          <div className="flex-shrink-0 w-8 h-8"></div> // Spacer
                        )}
                        {/* Audio Container 1 */}
                        {trackStatuses[0] === 'pending' ? (
                          // --- EDIT 2: Apply matching max-width to loading wrapper ---
                          <div className="flex-grow min-w-0 md:max-w-md"><LoadingComponent /></div>
                          // --- END EDIT 2 ---
                        ) : trackStatuses[0] === 'error' ? (
                          // --- EDIT 3: Apply matching max-width to error container ---
                          <div
                            id="audio-container-error-1"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-red-500/30 bg-red-900/20 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            <div className="w-11 h-11 flex items-center justify-center rounded-md mr-2.5 flex-shrink-0 bg-red-500/10">
                              <CircleExclamation className="w-6 h-6 text-red-400" />
                            </div>
                            <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                              <span className="font-normal text-base text-red-300 line-clamp-1">Generation Failed</span>
                              <span className="font-extralight text-sm text-red-400/80 line-clamp-1">Please try regenerating.</span>
                            </div>
                          </div>
                          // --- END EDIT 3 ---
                        ) : (
                          // --- EDIT 4: Apply matching max-width to audio container ---
                          <div
                            id="audio-container-1"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-white/10 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            {/* Streaming Indicator - Display based on state */}
                            {trackStatuses[0] === 'streaming' && (
                              <div className="absolute top-1 right-1 flex items-center gap-1 text-xs text-white/70 bg-black/30 px-1.5 py-0.5 rounded">
                                <div className="relative">
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full absolute top-0 left-0"></div>
                                </div>
                                {/* Removed Streaming... text */}
                              </div>
                            )}
                            {/* Audio Details */}
                            {currentResponse.audio && (
                              <>
                                <img
                                  src={currentResponse.avatar || "/project-images/Album_Art_6.webp"}
                                  alt="Cover"
                                  className="w-11 h-11 object-cover rounded-md mr-2.5 flex-shrink-0 transition-opacity duration-500 group-hover:opacity-60"
                                  onError={(e) => {
                                    if (e.target.src !== `${window.location.origin}/project-images/Album_Art_6.webp`) {
                                      e.target.onerror = null;
                                      e.target.src = "/project-images/Album_Art_6.webp";
                                    }
                                  }}
                                />
                                <div
                                  id="open-details-panel-from-ai-chatbox-1"
                                  className="flex flex-row cursor-pointer flex-grow w-full gap-1 min-w-0"
                                  onClick={() => handleAudioClick()}
                                >
                                  <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h2 className="font-normal text-base line-clamp-1">
                                        {currentResponse.audio.name}
                                      </h2>
                                      {/* --- EDIT 1: Dynamically set Version Badge for Track 1 --- */}
                                      <span className="flex justify-center items-center p-0.5 rounded bg-white/10 text-xs font-medium text-white/90">V{1}</span>
                                      {/* --- END EDIT 1 --- */}
                                    </div>
                                    <div className="flex flex-row w-full items-center gap-2 max-[640px]:gap-2 min-w-0">
                                      <span className="font-extralight text-sm text-[#878188] flex-grow w-full line-clamp-1 min-w-0">
                                        {currentResponse.musicDescription}
                                      </span>
                                      <span className="flex gap-1 text-[#878188] font-extralight text-sm justify-center items-center mt-auto flex-shrink-0">
                                        <Clock className="w-3.5" />
                                        {currentResponse.audio.duration}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {/* Play/Pause Button */}
                                {/* --- EDIT 7: Don't render play button if error --- */}
                                {trackStatuses[0] !== 'error' && (
                                  // --- END EDIT 7 ---
                                  <button
                                    id="play-pause-button-1"
                                    onClick={() => handlePlayPause(0)}
                                    className={`absolute flex flex-shrink-0 left-[1.125rem] items-center text-white transition-transform duration-500 transform group-hover:translate-y-0 ${isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 0 && !isBufferingFromContext
                                      ? ""
                                      : "md:translate-y-[300%] md:group-hover:opacity-100"
                                      }`}
                                  // --- EDIT 8: Remove disabled prop, handled by parent conditional render ---
                                  // disabled={trackStatuses[0] === 'error'}
                                  // --- END EDIT 8 ---
                                  >
                                    {/* --- EDIT 9: Update icon logic to show PlayFill when buffering --- */}
                                    {isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 0 && !isBufferingFromContext ? <PauseFill className="w-4 h-4" /> : <PlayFill className="w-4 h-4" />}
                                    {/* --- END EDIT 9 --- */}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- Track 2 --- */}
                    {(!isAnyTrackSelected || selectedTrackIndex === '1') && (
                      <div className="flex items-center gap-2 mb-2 max-w-md self-stretch">
                        {/* Checkbox Container 2 */}
                        {/* --- EDIT 2: Repeat logic for Track 2 --- */}
                        {currentResponse.audio && (trackStatuses[1] === 'streaming' || trackStatuses[1] === 'complete' || trackStatuses[1] === 'error') && (
                          <div
                            id="checkbox-container-2"
                            className={`
                              flex flex-shrink-0 w-8 h-8 px-2 justify-center items-center rounded-full bg-white/5 transition-colors duration-200
                              ${(!isLastMessage || trackStatuses[1] === 'streaming')
                                ? 'opacity-50 cursor-not-allowed' // Disabled styles
                                : 'hover:bg-white/10 cursor-pointer' // Enabled styles
                              }
                              ${selectedTracks[1] ? 'bg-white/10' : ''}
                            `}
                            onClick={() => {
                              // Prevent click if disabled
                              if (!isLastMessage || trackStatuses[1] === 'streaming') return;
                              handleCheckboxChange(1);
                            }}
                          >
                            {/* Render actual checkbox visual only when complete/error */}
                            {(trackStatuses[1] === 'complete' || trackStatuses[1] === 'error') && (
                              <div className={`w-4 h-4 flex-shrink-0 rounded-sm flex items-center justify-center ${selectedTracks[1] ? 'bg-white' : 'bg-transparent border border-white/30'}`}>
                                {selectedTracks[1] && <Check className="w-3 h-3 text-black" />}
                              </div>
                            )}
                          </div>
                        )}
                        {/* --- END EDIT 2 --- */}
                        {/* Spacer */}
                        {(!currentResponse.audio || trackStatuses[1] === 'pending') && message.trackCount > 1 && (
                          <div className="flex-shrink-0 w-8 h-8"></div>
                        )}
                        {/* Audio Container 2 */}
                        {trackStatuses[1] === 'pending' ? (
                          // --- EDIT 6: Apply matching max-width to loading wrapper ---
                          <div className="flex-grow min-w-0 md:max-w-md"><LoadingComponent /></div>
                          // --- END EDIT 6 ---
                        ) : trackStatuses[1] === 'error' ? (
                          // --- EDIT 7: Apply matching max-width to error container ---
                          <div
                            id="audio-container-error-2"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-red-500/30 bg-red-900/20 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            <div className="w-11 h-11 flex items-center justify-center rounded-md mr-2.5 flex-shrink-0 bg-red-500/10">
                              <CircleExclamation className="w-6 h-6 text-red-400" />
                            </div>
                            <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                              <span className="font-normal text-base text-red-300 line-clamp-1">Generation Failed</span>
                              <span className="font-extralight text-sm text-red-400/80 line-clamp-1">Please try regenerating.</span>
                            </div>
                          </div>
                          // --- END EDIT 7 ---
                        ) : (
                          // --- EDIT 8: Apply matching max-width to audio container ---
                          <div
                            id="audio-container-2"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-white/10 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            {/* Streaming indicator */}
                            {trackStatuses[1] === 'streaming' && (
                              <div className="absolute top-1 right-1 flex items-center gap-1 text-xs text-white/70 bg-black/30 px-1.5 py-0.5 rounded">
                                <div className="relative">
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full absolute top-0 left-0"></div>
                                </div>
                              </div>
                            )}
                            {/* Audio Details */}
                            {currentResponse.audio && (
                              <>
                                <img
                                  src={currentResponse.avatar || "/project-images/Album_Art_6.webp"}
                                  alt="Cover"
                                  className="w-11 h-11 object-cover rounded-md mr-2.5 flex-shrink-0 transition-opacity duration-500 group-hover:opacity-60"
                                  onError={(e) => {
                                    if (e.target.src !== `${window.location.origin}/project-images/Album_Art_6.webp`) {
                                      e.target.onerror = null;
                                      e.target.src = "/project-images/Album_Art_6.webp";
                                    }
                                  }}
                                />
                                <div
                                  id="open-details-panel-from-ai-chatbox-2"
                                  className="flex flex-row cursor-pointer flex-grow w-full gap-1 min-w-0"
                                  onClick={() => handleAudioClick()}
                                >
                                  <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h2 className="font-normal text-base line-clamp-1">
                                        {currentResponse.audio.name}
                                      </h2>
                                      {/* --- EDIT 2: Dynamically set Version Badge for Track 2 --- */}
                                      <span className="flex justify-center items-center p-0.5 rounded bg-white/10 text-xs font-medium text-white/90">V{2}</span>
                                      {/* --- END EDIT 2 --- */}
                                    </div>
                                    <div className="flex flex-row w-full items-center gap-2 max-[640px]:gap-2 min-w-0">
                                      <span className="font-extralight text-sm text-[#878188] flex-grow w-full line-clamp-1 min-w-0">
                                        {currentResponse.musicDescription}
                                      </span>
                                      <span className="flex gap-1 text-[#878188] font-extralight text-sm justify-center items-center mt-auto flex-shrink-0">
                                        <Clock className="w-3.5" />
                                        {currentResponse.audio.duration}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {/* Play/Pause Button */}
                                {/* --- EDIT 11: Repeat play button logic --- */}
                                {trackStatuses[1] !== 'error' && (
                                  <button
                                    id="play-pause-button-2"
                                    onClick={() => handlePlayPause(1)}
                                    className={`absolute flex flex-shrink-0 left-[1.125rem] items-center text-white transition-transform duration-500 transform group-hover:translate-y-0 ${isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 1 && !isBufferingFromContext
                                      ? ""
                                      : "md:translate-y-[300%] md:group-hover:opacity-100"
                                      }`}
                                  // disabled={trackStatuses[1] === 'error'} // Removed
                                  >
                                    {isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 1 && !isBufferingFromContext ? <PauseFill className="w-4 h-4" /> : <PlayFill className="w-4 h-4" />}
                                  </button>
                                )}
                                {/* --- END EDIT 11 --- */}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* --- Track 3 --- */}
                    {(!isAnyTrackSelected || selectedTrackIndex === '2') && (
                      <div className="flex items-center gap-2 mb-1 max-w-md self-stretch">
                        {/* Checkbox Container 3 */}
                        {/* --- EDIT 3: Repeat logic for Track 3 --- */}
                        {currentResponse.audio && (trackStatuses[2] === 'streaming' || trackStatuses[2] === 'complete' || trackStatuses[2] === 'error') && (
                          <div
                            id="checkbox-container-3"
                            className={`
                              flex flex-shrink-0 w-8 h-8 px-2 justify-center items-center rounded-full bg-white/5 transition-colors duration-200
                              ${(!isLastMessage || trackStatuses[2] === 'streaming')
                                ? 'opacity-50 cursor-not-allowed' // Disabled styles
                                : 'hover:bg-white/10 cursor-pointer' // Enabled styles
                              }
                              ${selectedTracks[2] ? 'bg-white/10' : ''}
                            `}
                            onClick={() => {
                              // Prevent click if disabled
                              if (!isLastMessage || trackStatuses[2] === 'streaming') return;
                              handleCheckboxChange(2);
                            }}
                          >
                            {/* Render actual checkbox visual only when complete/error */}
                            {(trackStatuses[2] === 'complete' || trackStatuses[2] === 'error') && (
                              <div className={`w-4 h-4 flex-shrink-0 rounded-sm flex items-center justify-center ${selectedTracks[2] ? 'bg-white' : 'bg-transparent border border-white/30'}`}>
                                {selectedTracks[2] && <Check className="w-3 h-3 text-black" />}
                              </div>
                            )}
                          </div>
                        )}
                        {/* --- END EDIT 3 --- */}
                        {/* Spacer */}
                        {(!currentResponse.audio || trackStatuses[2] === 'pending') && message.trackCount > 1 && (
                          <div className="flex-shrink-0 w-8 h-8"></div>
                        )}
                        {/* Audio Container 3 */}
                        {trackStatuses[2] === 'pending' ? (
                          // --- EDIT 10: Apply matching max-width to loading wrapper ---
                          <div className="flex-grow min-w-0 md:max-w-md"><LoadingComponent /></div>
                          // --- END EDIT 10 ---
                        ) : trackStatuses[2] === 'error' ? (
                          // --- EDIT 11: Apply matching max-width to error container ---
                          <div
                            id="audio-container-error-3"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-red-500/30 bg-red-900/20 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            <div className="w-11 h-11 flex items-center justify-center rounded-md mr-2.5 flex-shrink-0 bg-red-500/10">
                              <CircleExclamation className="w-6 h-6 text-red-400" />
                            </div>
                            <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                              <span className="font-normal text-base text-red-300 line-clamp-1">Generation Failed</span>
                              <span className="font-extralight text-sm text-red-400/80 line-clamp-1">Please try regenerating.</span>
                            </div>
                          </div>
                          // --- END EDIT 11 ---
                        ) : (
                          // --- EDIT 12: Apply matching max-width to audio container ---
                          <div
                            id="audio-container-3"
                            className="flex-grow flex items-center min-w-0 overflow-hidden rounded-lg border border-solid border-white/10 p-1.5 pr-2.5 relative md:max-w-md"
                          >
                            {/* Streaming indicator */}
                            {trackStatuses[2] === 'streaming' && (
                              <div className="absolute top-1 right-1 flex items-center gap-1 text-xs text-white/70 bg-black/30 px-1.5 py-0.5 rounded">
                                <div className="relative">
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full animate-ping"></div>
                                  <div style={{ background: "linear-gradient(135deg, rgba(252,16,242,0.90), rgba(48,120,228,0.90))" }} className="w-2 h-2 bg-blue-500 rounded-full absolute top-0 left-0"></div>
                                </div>
                              </div>
                            )}
                            {/* Audio Details */}
                            {currentResponse.audio && (
                              <>
                                <img
                                  src={currentResponse.avatar || "/project-images/Album_Art_6.webp"}
                                  alt="Cover"
                                  className="w-11 h-11 object-cover rounded-md mr-2.5 flex-shrink-0 transition-opacity duration-500 group-hover:opacity-60"
                                  onError={(e) => {
                                    if (e.target.src !== `${window.location.origin}/project-images/Album_Art_6.webp`) {
                                      e.target.onerror = null;
                                      e.target.src = "/project-images/Album_Art_6.webp";
                                    }
                                  }}
                                />
                                <div
                                  id="open-details-panel-from-ai-chatbox-3"
                                  className="flex flex-row cursor-pointer flex-grow w-full gap-1 min-w-0"
                                  onClick={() => handleAudioClick()}
                                >
                                  <div className="flex flex-col flex-grow w-full justify-center min-w-0">
                                    <div className="flex items-center gap-2">
                                      <h2 className="font-normal text-base line-clamp-1">
                                        {currentResponse.audio.name}
                                      </h2>
                                      {/* --- EDIT 3: Dynamically set Version Badge for Track 3 --- */}
                                      <span className="flex justify-center items-center p-0.5 rounded bg-white/10 text-xs font-medium text-white/90">V{3}</span>
                                      {/* --- END EDIT 3 --- */}
                                    </div>
                                    <div className="flex flex-row w-full items-center gap-2 max-[640px]:gap-2 min-w-0">
                                      <span className="font-extralight text-sm text-[#878188] flex-grow w-full line-clamp-1 min-w-0">
                                        {currentResponse.musicDescription}
                                      </span>
                                      <span className="flex gap-1 text-[#878188] font-extralight text-sm justify-center items-center mt-auto flex-shrink-0">
                                        <Clock className="w-3.5" />
                                        {currentResponse.audio.duration}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                {/* Play/Pause Button */}
                                {/* --- EDIT 13: Repeat play button logic --- */}
                                {trackStatuses[2] !== 'error' && (
                                  <button
                                    id="play-pause-button-3"
                                    onClick={() => handlePlayPause(2)}
                                    className={`absolute flex flex-shrink-0 left-[1.125rem] items-center text-white transition-transform duration-500 transform group-hover:translate-y-0 ${isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 2 && !isBufferingFromContext
                                      ? ""
                                      : "md:translate-y-[300%] md:group-hover:opacity-100"
                                      }`}
                                  // disabled={trackStatuses[2] === 'error'} // Removed
                                  >
                                    {isPlaying && contextAiResponseId === currentResponse.ai_response_id && playingTrackIndex === 2 && !isBufferingFromContext ? <PauseFill className="w-4 h-4" /> : <PlayFill className="w-4 h-4" />}
                                  </button>
                                )}
                                {/* --- END EDIT 13 --- */}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                {/* --- END: Track Rendering --- */}

                {/* ---------- Version toggle (Original / Regenerated) ---------- */}
                {responses.length > 1 && (
                  <div
                    id="response-version-toggle"
                    className="flex items-center space-x-1 text-xs text-[#878188] mb-1"
                  >
                    <button
                      onClick={handlePreviousResponse}
                      disabled={disableVersionToggle || currentResponseIndex === 0}
                      className={`p-1 hover:text-white ${disableVersionToggle || currentResponseIndex === 0
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                        }`}
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </button>
                    <span>{`${currentResponseIndex + 1}/${responses.length}`}</span>
                    <button
                      onClick={handleNextResponse}
                      disabled={
                        disableVersionToggle ||
                        currentResponseIndex === responses.length - 1
                      }
                      className={`p-1 hover:text-white ${disableVersionToggle ||
                          currentResponseIndex === responses.length - 1
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                        }`}
                    >
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {/* ---------------------------------------------------------------- */}

                <div
                  id="ai-response-action-buttons"
                  className={`mt-1 flex ${relatedMessages.length > 0
                    ? `pb-0`
                    : currentResponse.audio && `pb-0`
                    } space-x-0`}
                >
                  {/* Regenerate Button: Enabled only if it's the last message with audio AND no track is selected AND all tracks complete */}
                  {/* --- EDIT 11: Update Regenerate Button condition --- */}
                  {isLastMessage && currentResponse.audio && (
                    <button
                      id="regenerate-button"
                      onClick={() => {
                        // --- EDIT: Add console logs for debugging ---
                        console.log("[Regenerate Click] Button clicked.");
                        console.log("[Regenerate Click] isAnyTrackSelected:", isAnyTrackSelected);
                        console.log("[Regenerate Click] areAllTracksComplete:", areAllTracksComplete(trackStatuses));
                        // --- END EDIT ---

                        // Show toast if a track is selected, otherwise regenerate
                        // Also check if NOT all tracks are complete
                        if (isAnyTrackSelected) {
                          // --- EDIT: Add log ---
                          console.log("[Regenerate Click] Condition: isAnyTrackSelected is true. Showing toast.");
                          // --- END EDIT ---
                          setToastMessage("Please deselect the current track before regenerating.");
                          setToastType('info');
                        } else if (!areAllTracksComplete(trackStatuses)) {
                           // --- EDIT: Add log ---
                           console.log("[Regenerate Click] Condition: !areAllTracksComplete is true. Showing toast.");
                           // --- END EDIT ---
                           setToastMessage("Please wait for the current generation to complete before regenerating.");
                           setToastType('info');
                        } else {
                          // --- EDIT: Add log ---
                          console.log("[Regenerate Click] Conditions met. Calling onRegenerate with ID:", currentResponse.ai_response_id);
                          // --- END EDIT ---
                          // --- EDIT 2: Stop audio and reset autoplay states before regeneration ---
                          console.log(`[AIChatBox REG थ्री MAIN_BTN ${currentResponse.ai_response_id}] Attempting regeneration. Current context: isPlaying=${isPlaying}, contextAiResponseId=${contextAiResponseId}`);
                          if (isPlaying && contextAiResponseId === currentResponse.ai_response_id) {
                            console.log(`[AIChatBox REG थ्री MAIN_BTN ${currentResponse.ai_response_id}] Stopping currently playing audio for this message group before regeneration.`);
                            stopAudio();
                          }
                          setAutoplayInitiatedForThisMessage(false);
                          setIsAutoplaying(false);
                          setCurrentAutoplayTrackIndex(null);
                          autoplaySetupForResponseIdRef.current = null; // --- EDIT 4: Reset ref ---
                          // --- END EDIT 2 ---
                          setRegeneratingMessageId(currentResponse.ai_response_id);
                          onRegenerate(currentResponse.ai_response_id);
                        }
                      }}
                      onMouseEnter={() => setShowRegenerateTooltip(true)}
                      onMouseLeave={() => setShowRegenerateTooltip(false)}
                      // Disabled if any track IS selected OR if NOT ALL tracks complete
                      disabled={regeneratingMessageId === currentResponse.ai_response_id || isAnyTrackSelected || !areAllTracksComplete(trackStatuses) || localState.responseInProgress === true}
                      className={`hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-2 rounded-full text-[#878188] relative ${
                        (regeneratingMessageId === currentResponse.ai_response_id || isAnyTrackSelected || !areAllTracksComplete(trackStatuses) || localState.responseInProgress === true) ? 'opacity-50 cursor-not-allowed' : '' // Add disabled style
                      }`}
                    >
                      <ArrowsRotateLeft className="w-3.5 h-3.5" />
                      {showRegenerateTooltip && (
                        <div className="absolute left-6 top-10 bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded -translate-x-1/2 whitespace-nowrap z-10">
                          {isAnyTrackSelected ? "Deselect track to regenerate" : (!areAllTracksComplete(trackStatuses) ? "Generation in progress" : "Regenerate")}
                        </div>
                      )}
                    </button>
                  )}
                  {/* --- END EDIT 11 --- */}
                  {/* Other Action Buttons: Enabled only if a track IS selected AND ALL tracks complete */}
                  {/* --- EDIT 12: Update Other Action Buttons conditions --- */}
                  {currentResponse.audio && ( // Always render the container if there's audio
                    <div className="flex space-x-0">
                      <button
                        id="download-button"
                        onClick={() => {
                          // --- EDIT 20: Check completion again inside click ---
                          if (!areAllTracksComplete(trackStatuses)) {
                            setToastMessage("Please wait for generation to complete before downloading.");
                            setToastType('info');
                            return;

                          }

                          // if (!isAnyTrackSelected) {
                          //   setToastMessage("Please Select Atleast One Track.");
                          //   setToastType('info');
                          //   return; 
                          // }
                          // --- END EDIT 20 ---
                          triggerDownloadModal();
                        }}
                        // --- EDIT 1: Add mouse enter/leave for download tooltip (already present) ---
                        onMouseEnter={() => setShowDownloadTooltip(true)}
                        onMouseLeave={() => setShowDownloadTooltip(false)}
                        // --- END EDIT 1 ---
                        // Disabled if NO track is selected OR NOT ALL tracks complete
                        //  disabled={!isAnyTrackSelected || !areAllTracksComplete(trackStatuses)}
                        className={`  p-2 rounded-full text-[#878188] relative ${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} `}
                      >
                        {isDownloading ? (
                          <div className="w-4 h-4 border-2 border-t-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <ArrowShapeDownToLine className="w-3.5 h-3.5" />
                        )}
                        {showDownloadTooltip && (
                          <div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">
                            {areAllTracksComplete(trackStatuses) ? "Download" : "Generation in progress"}
                          </div>
                        )}
                      </button>
                      <button
                        id="like-button"
                        // --- EDIT 2: Add mouse enter/leave for like tooltip ---
                        onMouseEnter={() => setShowLikeTooltip(true)}
                        onMouseLeave={() => setShowLikeTooltip(false)}
                        // --- END EDIT 2 ---
                        onClick={() => {
                          if (!isAnyTrackSelected) {
                            setToastMessage("Please Select a Track First.");
                            setToastType('info');
                            return;
                          }
                          // No need to check completion for like/dislike
                          handleLikeDislike(true);
                        }}
                        // Disabled only if NO track is selected
                        //  disabled={!isAnyTrackSelected}
                        className={` p-2 rounded-full text-[#878188] relative ${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"}`}
                      >
                        {likeStatuses[currentResponse.ai_response_id] ===
                          "like" ? (
                          <ThumbsUpFill className="w-3.5 h-3.5 text-white" />
                        ) : (
                          <ThumbsUp className="w-3.5 h-3.5" />
                        )}
                        {showLikeTooltip && (<div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">{likeStatuses[currentResponse.ai_response_id] === "like" ? "Unlike" : "Like"}</div>)}
                      </button>
                      <button
                        id="dislike-button"
                        // --- EDIT 3: Add mouse enter/leave for dislike tooltip ---
                        onMouseEnter={() => setShowDislikeTooltip(true)}
                        onMouseLeave={() => setShowDislikeTooltip(false)}
                        // --- END EDIT 3 ---
                        onClick={() => {
                          if (!isAnyTrackSelected) {
                            setToastMessage("Please Select a Track First.");
                            setToastType('info');
                            return;
                          }
                          // No need to check completion for like/dislike
                          handleLikeDislike(false);
                        }}
                        // Disabled only if NO track is selected
                        //  disabled={!isAnyTrackSelected}
                        className={` p-2 rounded-full text-[#878188] relative ${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} `}
                      >
                        {likeStatuses[currentResponse.ai_response_id] ===
                          "dislike" ? (
                          <ThumbsDownFill className="w-3.5 h-3.5 text-white" />
                        ) : (
                          <ThumbsDown className="w-3.5 h-3.5" />
                        )}
                        {showDislikeTooltip && (<div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">Dislike</div>)}
                      </button>
                      <button
                        id="nodes-right-button"
                        onClick={() => {
                          // --- EDIT 21: Check completion again inside click ---
                          if (!areAllTracksComplete(trackStatuses)) {
                            setToastMessage("Please wait for generation to complete before getting stems.");
                            setToastType('info');
                            return;
                          }
                          if (!isAnyTrackSelected) {
                            setToastMessage("Please Select a Track First.");
                            setToastType('info');
                            return;
                          }
                          // --- END EDIT 21 ---
                          handleStemToast();
                        }}
                        // --- EDIT 4: Add mouse enter/leave for nodes tooltip ---
                        onMouseEnter={() => setShowNodesRightTooltip(true)}
                        onMouseLeave={() => setShowNodesRightTooltip(false)}
                        // --- END EDIT 4 ---
                        // Disabled if NO track is selected OR NOT ALL tracks complete
                        //  disabled={!isAnyTrackSelected || !areAllTracksComplete(trackStatuses)}
                        className={`${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} p-2 rounded-full text-[#878188] relative`}
                      >
                        <img
                          src="/updates-modal/feature-update-icon-2.svg"
                          alt="Get stems"
                          className="w-3.5 h-3.5 opacity-50 hover:opacity-100"
                        />
                        {showNodesRightTooltip && (
                          <div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">
                            {areAllTracksComplete(trackStatuses) ? "Get stems" : "Generation in progress"}
                          </div>
                        )}
                      </button>
                      <button
                        id="eye-button"
                        onClick={() => {
                          // --- EDIT 22: Check completion again inside click ---
                          if (!areAllTracksComplete(trackStatuses)) {
                            setToastMessage("Please wait for generation to complete before previewing.");
                            setToastType('info');
                            return;
                          }
                          if (!isAnyTrackSelected) {
                            setToastMessage("Please Select a Track First.");
                            setToastType('info');
                            return;
                          }
                          // --- END EDIT 22 ---
                          handleEyeClick();
                        }}
                        // --- EDIT 5: Add mouse enter/leave for eye tooltip ---
                        onMouseEnter={() => setShowEyeTooltip(true)}
                        onMouseLeave={() => setShowEyeTooltip(false)}
                        // --- END EDIT 5 ---
                        // Disabled if NO track is selected OR NOT ALL tracks complete
                        //  disabled={!isAnyTrackSelected || !areAllTracksComplete(trackStatuses)}
                        className={`${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} p-2 rounded-full text-[#878188] relative`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        {showEyeTooltip && (
                          <div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">
                            {areAllTracksComplete(trackStatuses) ? "Preview content" : "Generation in progress"}
                          </div>
                        )}
                      </button>
                      <button
                        id="share-button"
                        onClick={() => {
                          // --- EDIT 23: Check completion again inside click ---
                          if (!areAllTracksComplete(trackStatuses)) {
                            setToastMessage("Please wait for generation to complete before sharing.");
                            setToastType('info');
                            return;
                          }
                          if (!isAnyTrackSelected) {
                            setToastMessage("Please Select a Track First.");
                            setToastType('info');
                            return;
                          }
                          // --- END EDIT 23 ---
                          handleShare();
                        }}
                        // Disabled if NO track is selected OR isSharing OR NOT ALL tracks complete
                        //  disabled={!isAnyTrackSelected || isSharing || !areAllTracksComplete(trackStatuses)}
                        disabled={isSharing}
                        // --- EDIT 6: Add mouse enter/leave for share tooltip ---
                        onMouseEnter={() => setShowShareTooltip(true)}
                        onMouseLeave={() => setShowShareTooltip(false)}
                        // --- END EDIT 6 ---
                        className={`${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} p-2 rounded-full text-[#878188] relative`}
                      >
                        {isSharing ? (
                          <div className="w-3.5 h-3.5 border-2 border-t-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <ArrowUpFromSquare className="w-3.5 h-3.5" />
                        )}
                        {showShareTooltip && !isSharing && (
                          <div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">
                            {areAllTracksComplete(trackStatuses) ? "Share" : "Generation in progress"}
                          </div>
                        )}
                      </button>

                      {/* New Advanced Edit Button */}
                      <div
                        className="relative group"
                        onMouseEnter={() => setShowAdvancedEditTooltip(true)}
                        onMouseLeave={() => setShowAdvancedEditTooltip(false)}
                      >
                        <button
                          id="ai-response-advanced-edit-button"
                          aria-label="advanced edit"
                          // Disabled if NO track is selected OR NOT ALL tracks complete
                          //  disabled={!isAnyTrackSelected || !areAllTracksComplete(trackStatuses)}
                          className={`${!areAllTracksComplete(trackStatuses) ? `opacity-50 hover:bg-[rgba(255,255,255,0.05)]` : "hover:text-white"} p-2 rounded-full text-[#878188] relative `}
                          onClick={() => {
                            // --- EDIT 24: Check completion again inside click ---
                            if (!areAllTracksComplete(trackStatuses)) {
                              setToastMessage("Please wait for generation to complete before editing.");
                              setToastType('info');
                              return;
                            }

                            if (!isAnyTrackSelected) {
                              setToastMessage("Please Select a Track First.");
                              setToastType('info');
                              return;
                            }
                            // --- END EDIT 24 ---
                            handleAdvancedEdit();
                          }}
                        >
                          <Sliders className="w-3.5 h-3.5" />
                        </button>
                        {showAdvancedEditTooltip && (
                          <div className="absolute bg-[#2C2A2C] text-white text-xs p-1 pl-1.5 rounded top-10 left-6 -translate-x-1/2 whitespace-nowrap z-10">
                            {areAllTracksComplete(trackStatuses) ? "Advanced edit" : "Generation in progress"}
                          </div>
                        )}
                      </div>
                    </div>
                  )} 
                  {/* --- END EDIT 12 --- */}
                </div>
              </div>
            </div>
          </div>
        )}
        {isDownloadModalOpen && (
          <DownloadModal
            title={currentResponse.audio.name}
            description={currentResponse.musicDescription}
            duration={currentResponse.audio.duration}
            imageSrc={currentResponse.avatar}
            ai_response_id={currentResponse.ai_response_id}
            trackIndex={parseInt(selectedTrackIndex, 10)} // This was correctly passed
            onClose={() => setIsDownloadModalOpen(false)}
            onError={(message) => {
              setToastMessage(message);
              setToastType("error");
              setIsDownloadModalOpen(false);
            }}
          />
        )}
        {isGeneratingStems ? (
          <ProgressInfoToast
            type="info"
            message={`${stemTrackName || 'Track'} - Stems are being generated...`}
            loading={true}
            excludeCloseButton={false}
            className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
            imageSrc={currentResponse.avatar}
            currentStep={stemProgress}
            totalSteps={3}
            onClose={() => setIsGeneratingStems(false)}
          />
        ) : toastMessage && (
          <Toast
            type={toastType}
            message={toastMessage}
            onClose={() => setToastMessage(null)}
          />
        )}

        {isFeedbackModalOpen && (
          <Modal
            closeModal={() => setIsFeedbackModalOpen(false)}
            showCloseButton={false}
          >
            <FeedbackContent
              responseId={currentResponse.ai_response_id}
              onClose={() => setIsFeedbackModalOpen(false)}
              onSubmit={async () => {
                setToastMessage(
                  "We're sorry! We added the credits for this track back to your account 🎵"
                );
                setToastType("success");
                // Add your submit logic here
                setIsFeedbackModalOpen(false);
              }}
            />
          </Modal>
        )}
        {/* Options Modal for small screens */}
        {isOptionsModalOpen && (
          <Modal closeModal={handleOptionsClose}>
            <div className="flex flex-col gap-0 w-full px-2 pt-10 pb-3 bg-[#1d1b1d] rounded-lg">
              {isLastMessage && currentResponse.audio && (
                <button
                  id="regenerate-button"
                  onClick={() => {
                    // --- EDIT 2: Reset autoplay states before regeneration ---
                    setAutoplayInitiatedForThisMessage(false);
                    setIsAutoplaying(false);
                    setCurrentAutoplayTrackIndex(null);
                    // --- END EDIT 2 ---
                    onRegenerate(currentResponse.ai_response_id);
                    handleOptionsClose();
                  }}
                  className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                >
                  <ArrowsRotateLeft className="w-5 h-5" />
                  <span>Regenerate</span>
                </button>
              )}
              <button
                onClick={() => {
                  handleEyeClick();
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                <Eye className="w-5 h-5" />
                <span>Preview</span>
              </button>
              <button
                onClick={() => {
                  handleStemToast();
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                <img
                  src="/updates-modal/feature-update-icon-2.svg"
                  alt="Get stems"
                  className="w-5 h-5"
                />
                <span>Nodes</span>
              </button>
              <button
                onClick={() => {
                  setIsDownloadModalOpen(true);
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                <ArrowShapeDownToLine className="w-5 h-5" />
                <span>Download</span>
              </button>
              <button
                id="like-button"
                onClick={() => {
                  handleLikeDislike(true);
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                {likeStatuses[currentResponse.ai_response_id] === "like" ? (
                  <ThumbsUpFill className="w-5 h-5" />
                ) : (
                  <ThumbsUp className="w-5 h-5" />
                )}
                <span>Like</span>
              </button>
              <button
                id="dislike-button"
                onClick={() => {
                  handleLikeDislike(false);
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                {likeStatuses[currentResponse.ai_response_id] === "dislike" ? (
                  <ThumbsDownFill className="w-5 h-5" />
                ) : (
                  <ThumbsDown className="w-5 h-5" />
                )}
                <span>Dislike</span>
              </button>
              <button
                id="share-button"
                onClick={() => {
                  handleShare();
                  handleOptionsClose();
                }}
                className="flex items-center gap-3 hover:text-white hover:bg-[rgba(255,255,255,0.05)] p-3 rounded-md text-[#878188] transition-colors duration-300"
                disabled={!whatsNextEnabled}
              >
                <ArrowUpFromSquare className="w-5 h-5" />
                <span>Share</span>
              </button>
            </div>
          </Modal>
        )}
      </div>
      {isLastMessage && currentResponse.audio && !currentResponse.isStopped && (
        <div className="flex px-6 justify-end w-full">
          {/* Empty div for spacing on the left side */}
          <div className="flex flex-col gap-1 w-full max-w-96 md:max-w-[560px]">
            <div className="flex w-full items-center justify-end gap-1">
              <h3 className="text-white text-right text-sm">What's next?</h3>
            </div>

            <div className="flex flex-wrap gap-2 justify-end">
              {suggestionsLoading ? (
                <>
                  {/* Skeleton loaders for suggestions */}
                  {[1, 2, 3, 4, 5].map((item) => (
                    <div key={item} className="h-8 rounded-lg bg-white/5 animate-pulse" style={{ width: `${Math.floor(Math.random() * 40) + 100}px` }}></div>
                  ))}
                </>
              ) : (
                <>
                  {/* Use our helper function to get the suggestions safely */}
                  {(() => {
                    const suggestions = getSuggestions();

                    if (suggestions && suggestions.length > 0) {
                      return suggestions.map((suggestion, index) => (
                        <div key={index} className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                          <button
                            onClick={() => handleActionClick(suggestion)}
                            // Disable if NO track is selected OR action is loading OR NOT ALL tracks complete
                            disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                            className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188] group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                              // Add disabled styles conditionally
                              (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                          >
                            <span>{suggestion}</span>
                            {isActionLoading && actionLoadingText === suggestion ? (
                              <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Music action" />
                            )}
                          </button>
                        </div>
                      ));
                    } else {
                      // Fallback to the current hardcoded UI if no suggestions are available
                      return (
                        <>
                          <div className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                            <button
                              onClick={() => handleActionClick("Extend to 2 minutes")}
                              // Disable if NO track is selected OR action is loading OR original condition met OR NOT ALL tracks complete
                              disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                              className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188]  group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                                // Add disabled styles conditionally
                                (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                              <span>Extend track duration</span>
                              {isActionLoading && actionLoadingText === "Extend to 2 minutes" ? (
                                <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Change duration" />
                              )}
                            </button>
                          </div>
                          <div className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                            <button
                              onClick={() => handleActionClick("Change theme to retro")}
                              // Disable if NO track is selected OR action is loading OR original condition met OR NOT ALL tracks complete
                              disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                              className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188]  group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                                // Add disabled styles conditionally
                                (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                              <span>Soften synth with piano</span>
                              {isActionLoading && actionLoadingText === "Change theme to retro" ? (
                                <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Change theme" />
                              )}
                            </button>
                          </div>
                          {/* Repeat for other fallback suggestions */}
                          <div className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                            <button
                              onClick={() => handleActionClick("Switch genre to jazz")}
                              // Disable if NO track is selected OR action is loading OR original condition met OR NOT ALL tracks complete
                              disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                              className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188]  group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                                // Add disabled styles conditionally
                                (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                              <span>Intensify ambient layers</span>
                              {isActionLoading && actionLoadingText === "Switch genre to jazz" ? (
                                <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Change genre" />
                              )}
                            </button>
                          </div>
                          <div className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                            <button
                              onClick={() => handleActionClick("Add piano and strings")}
                              // Disable if NO track is selected OR action is loading OR original condition met OR NOT ALL tracks complete
                              disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                              className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188]  group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                                // Add disabled styles conditionally
                                (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                              <span>Make genre more techno</span>
                              {isActionLoading && actionLoadingText === "Add piano and strings" ? (
                                <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Change instrument" />
                              )}
                            </button>
                          </div>
                          <div className="group flex border border-white/10 hover:border-white/0 p-px rounded-lg hover:bg-gradient-to-r from-[#fc10f2] to-[#3078e4] transition duration-500">
                            <button
                              onClick={() => handleActionClick("Make it more upbeat")}
                              // Disable if NO track is selected OR action is loading OR original condition met OR NOT ALL tracks complete
                              disabled={!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)}
                              className={`px-2 h-8 rounded-lg group-hover:bg-[#100F10] text-[#878188]  group-hover:text-white font-normal text-sm flex items-center gap-1 ${
                                // Add disabled styles conditionally
                                (!isAnyTrackSelected || !whatsNextEnabled || isActionLoading || !areAllTracksComplete(trackStatuses)) ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                            >
                              <span>Shift mood to melancholic</span>
                              {isActionLoading && actionLoadingText === "Make it more upbeat" ? (
                                <div className="h-4 w-4 border-2 border-t-1 border-white border-t-transparent rounded-full animate-spin"></div>
                              ) : (
                                <img src="/MusicGenerateEnabled.svg" className={`h-4 w-4 opacity-40 ${areAllTracksComplete(trackStatuses) ? 'group-hover:opacity-100' : ''}`} alt="Change mood" />
                              )}
                            </button>
                          </div>
                        </>
                      );
                    }
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {showFeedbackPrompt &&
        !message.is_error &&
        isLastMessage &&
        !message.isStopped &&
        message.responseType !== "stopped" && (
          <FeedbackPrompt
            onLike={() => {
              // posthog.capture(
              //   "Click on Like Icon button on First Time Feedback"
              // );
              handleLikeDislike(true);
            }}
            onDislike={() => {
              // posthog.capture(
              //   "Click on Dislike Icon button on First Time Feedback"
              // );
              handleLikeDislike(false);
            }}
            onDismiss={handleFeedbackDismiss}
            likeStatus={likeStatuses[currentResponse.ai_response_id]}
          />
        )}
      {isShareModalOpen && (
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          shareUrl={generatedShareUrl}
          trackInfo={{
            name: currentResponse.audio?.name || "Generated Music",
            description: currentResponse.musicDescription || "",
            coverImage: currentResponse.avatar || "/logo.png",
          }}
        />
      )}

      {isAdvancedEditModalOpen && (
        <AdvancedEditModal
          onClose={handleCloseAdvancedEditModal}
          initialAudioUri={audioUriForAdvancedEdit}
          projectId={currentResponse.projectId || projectId} // Pass projectId
          responseId={currentResponse.ai_response_id}   // Pass responseId (original ID of the multi-track response)
        />
      )}
    </div>
  );
};

export default AIChatBox;